export const languageEnglish = {
  concurrentResourceChanged: 'This chat or setting changed in another page. Wait for synchronization, then retry.',
  iris: {
    intro: "Hello there. I've been waiting for you.",
    introTip: 'Iris can access various data through the Risuai system. It uses ax model defined in config.',
    unsupportedModel:
      "It seems your current model doesn't support me responding... Please switch to a compatible model, like GPT, Claude, or Gemini which are not plugins.",
  },
  messageRequests: {
    chapterScore: 'Score',
    chapterThreshold: 'Threshold',
    chapterExchanges: 'Completed turns',
    chapterClosed: 'Closed',
    chapterPartial: 'Closed with partial progress',
    chapterDetails: 'Chapter details',
    chapterRequests: 'Chapter memory LLM requests and responses',
    chapterDecision: 'Final assessment',
    chapterSummary: 'Chapter memory',
    chapterCompleted: 'Completed',
    chapterFailed: 'Failed',
    chapterPending: 'Summarizing',
    chapterSummaryFailed: 'Summary failed',

    chapterMemory: 'Chapter memory',
    sentRequest: 'Sent provider request',
    sentRequestHint: 'Body captured at send time',
    sentRequestUnavailable:
      'No final provider request was captured for this record. Only the internal assembly is available; it is not reconstructed as a historical sent request.',
    sentRequestOmitted: 'Some request bodies exceeded the recording limit and were omitted. This view is incomplete.',
    assembly: 'Internal assembly (examples and boundary markers)',
    open: 'LLM details',
    title: 'LLM requests and responses for this reply',
    refresh: 'Refresh',
    loading: 'Loading request details…',
    empty:
      'No retained request records match this reply. Older records may have been removed, or no model request was sent.',
    disabled: 'Request recording is disabled. Enable it in Settings → Request History to inspect future requests.',
    prompt: 'Prompt · assembly record',
    promptHint:
      'This record contains the assembled prompt, including author examples and internal markers. Provider conversion happens afterward, so this is not the final provider request.',
    system: 'System prompt',
    example: 'Author example (not conversation history)',
    exampleUser: 'User',
    exampleAssistant: 'Character',
    greeting: 'Character greeting',
    chatMessage: 'Conversation message',
    message: 'Message',
    exampleBoundary: 'Example boundary',
    chatBoundary: 'Conversation begins',
    internalBoundary: 'Internal boundary marker',
    response: 'Response · model output',
    details: 'Model settings, token usage and raw internal record',
  },
  storyChapterGoal: 'Chapter goal',
  storyChapterLabel: (number: number) => `Chapter ${number}`,
  emptyTrash: 'Empty trash',
  emptyTrashBusy: 'Emptying trash…',
  emptyTrashConfirm: (count: number) =>
    `Permanently delete all ${count} characters in the trash, including their conversations? This includes characters hidden by the search filter. This cannot be undone.`,
  emptyTrashProgress: (done: number, total: number) => `Processed ${done} of ${total} characters.`,
  emptyTrashResult: (deleted: number, queued: number, failed: number, skipped: number, remaining: number) =>
    `Deleted: ${deleted}; pending sync: ${queued}; failed: ${failed}; skipped (state changed): ${skipped}; not processed: ${remaining}. Queued or failed operations stop the batch.`,
  discovery: {
    pageUnavailable: 'This page does not exist or has been removed.',
    imageUnavailable: 'Image unavailable',
    viewCard: 'View details',
    home: 'Home',
    chats: 'Chats',
    explore: 'Explore',
    meetCharacters: 'Meet characters',
    enterStories: 'Step into stories',
    hero: {
      eyebrow: 'AI companions · Interactive stories',
      titleStart: 'Good company,',
      titleEnd: 'Great stories.',
      description: 'Chat about your day and shape a story of your own.',
    },
    all: 'All',
    resume: 'Continue chatting',
    yesterday: 'Yesterday',
    conversationDate: (month: number, day: number, year?: number) =>
      new Date(year ?? 2000, month - 1, day).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(year === undefined ? {} : { year: 'numeric' }),
      }),
    allChats: 'All conversations',
    characters: 'Characters',
    stories: 'Stories',
    allCharacters: 'All characters',
    allStories: 'All stories',
    noChats: 'No conversations to continue. Choose a character or story in the card library.',
    chatScope:
      'Only the selected user’s character and story conversations are shown. Each published card has one current conversation per user, pinned first and then by recent activity.',
  },
  consoleChat: {
    catalogAll: 'All content',
    catalogCategory: 'Content category',
    catalogSearch: 'Search names…',
    catalogEmpty: 'No matching content',
    catalogReset: 'View all',
    catalogStory: 'Story',
    catalogCategories: {
      companionship: 'Companionship',
      careers: 'Careers',
      family: 'Family',
      life: 'Life',
      arts: 'Arts',
      healthcare: 'Healthcare',
    },
    currentUser: 'Current chat user',
    chooseUser: 'Choose chat user',
    switchUser: 'Switch user',
    pickerTitle: 'Choose a WeChat user',
    selectForChat: 'Use for chatting',
    newConversation: 'New conversation',
    continueChat: 'Continue chatting',
    owner: 'Conversation owner',
    unbound: 'No user assigned',
    differentOwner: 'This conversation belongs to another user. Select its owner or start from the card library.',
    pickThenNew: 'Unassigned history stays unchanged. Choose a user, then start from the card library.',
    restoreFailed: 'Could not verify your previous user. Retry or choose again.',
    retry: 'Retry',
    close: 'Close',
    clear: 'Clear selection',
    memory: 'Mem0 memory',
    memoryUnknown: 'This historical request has no memory diagnostics.',
    memoryUnbound: 'Disabled: no StoryTune user assigned',
    memoryDisabled: 'Disabled: Mem0 is switched off',
    memoryIneligible: 'Skipped: conversation is not eligible for Mem0',
    memoryNotInitialized: 'Preview skipped: memory has not been initialized',
    memoryEmptyQuery: 'Skipped: no user input',
    memoryUnavailable: 'Memory retrieval unavailable',
    memorySuspended: 'Memory suspended',
    memoryServiceMissing: 'Skipped: memory runtime not registered',
    chooseCharacter: 'Choose a character',
    noCharacters: 'No published character cards available',
    createFailed: 'Could not prepare conversation. Try again.',
    selected: 'Selected',
    userId: 'User ID',
    memoryCounts: (retrieved: number, selected: number, injected: number) =>
      `Retrieved ${retrieved}, selected ${selected}, injected ${injected}`,
  },
  adminUsers: {
    search: 'Search nickname or user ID',
    phone: 'Phone users',
    wechat: 'WeChat users',
    unnamed: 'No nickname',
    empty: 'No records found',
    loading: 'Loading…',
    failed: 'Could not load records. Please retry.',
    previous: 'Previous',
    next: 'Next',
  },
  unknownInteractionTime: 'Unknown',
  generationReattachFailure: {
    message: 'This reply may still be running, but this device could not reconnect.',
    lastError: (error: string) => `Last connection error: ${error}`,
    retry: 'Retry',
    refresh: 'Refresh',
    stop: 'Stop',
    sidebarWarning: (name: string) => `Connection lost: ${name}`,
  },
  preloadOfflineError:
    'A part of the app could not load while offline. Your current page is still open; try the action again after the connection returns.',
  preloadLoadError:
    'Part of this page could not load. The network or server may be unavailable, or the page version may have changed. Retry; if it still fails, reload the page.',
  preloadReload: 'Reload',
  pluginRuntime: {
    failed: 'Plugins could not start. The rest of the app is still available.',
    retry: 'Retry plugins',
    retrying: 'Retrying plugins…',
  },
  loadingCharacter: 'Loading character…',
  characterDataLoadFailed: 'Character data could not be loaded.',
  characterCreationQueued:
    'The new character is saved on this device and queued. It has not been accepted by the server and will retry automatically.',
  characterCreationFailed: 'The character could not be created. The provisional character was removed.',
  characterImportQueued:
    'The imported character is saved on this device and queued. It has not been accepted by the server and will retry automatically.',
  characterImportFailed: 'The imported character could not be saved. The provisional character was removed.',
  characterImportDroppedArchiveEntry: (fileName: string) => `Archive file: ${fileName}`,
  characterImportDroppedInlineAsset: (index: number, name: string) =>
    `Inline asset data.assets[${index}]${name ? ` (${name})` : ''}`,
  characterImportIncomplete: (details: string) =>
    `Character imported, but the following oversized content was dropped:\n${details}`,
  characterImportFailedAfterDroppedContent: (details: string) =>
    `No readable character card could be imported. The following oversized content was dropped:\n${details}`,
  mutationStatusQueued: 'Queued',
  mutationStatusFailed: 'Save failed',
  messageMutationPending: 'Saving message change…',
  messageMutationQueued: 'This message change is saved on this device and queued. It will retry automatically.',
  messageMutationFailed: 'The message change could not be saved. The previous transcript was restored.',
  messageMutationStale: 'The message changed before this action could be saved. Review it and try again.',
  hypaMemoryMutationQueued: 'The Hypa Memory change is saved on this device and queued. It will retry automatically.',
  hypaMemoryMutationFailed: 'The Hypa Memory change could not be saved. The previous setting was restored.',
  hypaContextTruncationConfirm:
    'Older messages may be omitted from this request because Hypa Memory is not enabled for this chat. Continue without Hypa Memory? Your choice will be remembered for this chat.',
  characterRemovalPending: (name: string) => `Moving ${name} to the trash…`,
  characterRemovalQueued: (name: string) =>
    `${name} was moved to the trash on this device and the change is queued. It will retry automatically.`,
  characterRemovalFailed: (name: string) => `${name} could not be moved to the trash. The character was restored.`,
  characterRestorePending: (name: string) => `Restoring ${name}…`,
  characterRestoreQueued: (name: string) =>
    `${name} was restored on this device and the change is queued. It will retry automatically.`,
  characterRestoreUnavailable: (name: string) =>
    `${name} cannot be restored because this legacy row has no persistent character ID.`,
  characterRestoreFailed: (name: string) => `${name} could not be restored. The character remains in the trash.`,
  characterPermanentDeletePending: (name: string) => `Deleting ${name} permanently…`,
  characterPermanentDeleteQueued: (name: string) =>
    `${name} was deleted on this device and the permanent deletion is queued. It will retry automatically.`,
  characterPermanentDeleteFailed: (name: string) =>
    `${name} could not be deleted permanently. The character was restored.`,
  characterOrganizationSaving: 'Saving…',
  characterOrganizationPending: 'Saving the character order…',
  characterOrganizationQueued:
    'The character organization change is saved on this device and queued. It will retry automatically.',
  characterOrganizationFailed: 'The character organization change could not be saved. The previous order was restored.',
  characterFolderOrganizationPending: (name: string) => `Saving changes to ${name}…`,
  characterFolderOrganizationQueued: (name: string) =>
    `Changes to ${name} are saved on this device and queued. They will retry automatically.`,
  characterFolderOrganizationFailed: (name: string) =>
    `Changes to ${name} could not be saved. The previous folder settings were restored.`,
  bookmarkRenamePending: (name: string) => `Saving the new name for ${name}…`,
  bookmarkRenameQueued: (name: string) =>
    `The new name for ${name} is saved on this device and queued. It will retry automatically.`,
  bookmarkRenameFailed: (name: string) =>
    `The new name for ${name} could not be saved. The previous bookmark name was restored.`,
  bookmarkRemovePending: (name: string) => `Removing ${name}…`,
  bookmarkRemoveQueued: (name: string) =>
    `${name} was removed on this device and the change is queued. It will retry automatically.`,
  bookmarkRemoveFailed: (name: string) => `${name} could not be removed. The bookmark was restored.`,
  settingsSaveQueued: 'This settings change is saved on this device and queued. It will retry automatically.',
  regexDisplayUpdatePending: 'Chat display will update when editing pauses.',
  chatGenerationSettingsSaving: 'Saving chat generation settings…',
  chatGenerationSettingsTargetChanged:
    'The active chat changed before this selection could be saved. Reopen the picker and try again.',
  chatGenerationSettingsSaveFailed: (detail: string) =>
    `Chat generation settings could not be saved.${detail ? ` ${detail}` : ''}`,
  generationPersistenceQueued: 'This reply is waiting to be saved and remains provisional.',
  generationPersistenceStalled:
    'Saving this provisional reply keeps failing. It remains queued and will retry automatically.',
  generationPersistenceTerminal:
    'This provisional reply could not be saved safely and will not retry automatically. Its journal record has been retained.',
  generationPersistenceStalledLegacy:
    'This restored provisional reply cannot be retried safely because its legacy recovery record has no transcript snapshot.',
  composerFileResultDiscarded:
    'The uploaded file could not be attached because the active composer changed. Please attach it again.',
  composerDraftRecovery: {
    storageFailed:
      'This composer draft could not be stored for reload recovery. Keep this tab open or copy the text before leaving.',
    queuedSaveFailed: 'The queued message was not accepted. Your composer draft was kept so you can retry.',
  },
  acceptedSendRecovery: {
    superseded:
      'Newer messages exist, so this failed reply cannot be retried. Dismiss this notice to keep the saved conversation as it is.',
    dismiss: 'Dismiss',
    dismissFailed: 'Could not dismiss. Please try again.',
    retryFailed: 'The reply retry did not succeed. Try again or dismiss this notice.',

    generationFailed:
      'Your message was saved, but its reply could not be started. Retry without sending the message again.',
    generationInProgress:
      'Your message was saved, but another client or session was already generating in this chat. Retry the reply after that generation finishes.',
    abandoned: 'Your message was saved, but its reply was interrupted by a server restart. You can retry the reply.',
    providerMayHaveRun: 'The previous provider request may already have run and may have been billed.',
    providerMayHaveRunConfirm:
      'The previous provider request may already have been billed. Retrying may create another billed request. Retry anyway?',
    retry: 'Retry reply',
    retrying: 'Retrying…',
  },
  generationStop: {
    stopping: 'Stopping…',
    failed: 'Stop could not be acknowledged. The generation may still be running.',
    retry: 'Retry Stop',
    savingStoppedPartial: 'Stopped. Saving the partial reply…',
  },
  fileSelectionStale: 'Settings changed while you were choosing the file. Please choose it again to retry.',
  alternateGreetingMutationQueued:
    'The alternate greeting change is saved on this device and queued. It will retry automatically.',
  alternateGreetingMutationFailed:
    'The alternate greeting change could not be saved. The previous greeting order was restored.',
  retainedChatMutationFailed: 'A queued chat change was rejected. The previous value was restored.',
  secretInput: {
    savedPlaceholder: 'Saved credential',
    savedStatus: 'A credential is saved.',
    clearSaved: 'Clear saved credential',
  },
  formating: {
    main: 'Main Prompt',
    jailbreak: 'Jailbreak Prompt',
    chats: 'Past Chats',
    lorebook: 'Lorebook',
    globalNote: 'Global Note',
    authorNote: "Author's Note",
    lastChat: 'Last Chat',
    description: 'Character Description',
    personaPrompt: 'Persona Prompt',
    plain: 'Plain Prompt',
    memory: 'Supa/HypaMemory',
    postEverything: 'End-Injected Prompts',
  },
  errors: {
    toomuchtoken: 'Error: The minimum required token is greater than the Max Context Size.',
    settingsSaveFailed: 'Settings could not be saved. Please try again.',
    sendContextPersistenceFailed: 'Chat setup could not be saved before generation. Please try again.',
    replyStillSaving: 'The previous reply is still saving. Please wait a moment and try again.',
    hypaContextTruncationAcknowledgementFailed:
      'Your choice to continue without Hypa Memory could not be saved for this chat. Please try again.',
    chatImportTooLarge: 'This chat contains metadata or a single message that is too large to import safely.',
    promptTemplateUnavailable: 'Prompt template is still loading. Please try again after server sync finishes.',
    unknownModel: 'Error: Unknown model selected',
    httpError: 'Error: error in request:',
    toolFollowupRequestFailed: 'Failed to fetch model response after tool execution',
    ollamaCloudToolPostRequired: 'Ollama Cloud tool proxy only accepts POST requests with a body',
    ollamaCloudToolsUnsupported: 'The selected Ollama Cloud request format does not support MCP tools.',
    ollamaToolCallLimit: 'Ollama tool call limit reached',
    openAIResponsesToolCallLimit: 'OpenAI Responses tool call limit reached',
    anthropicCredentialInvalid: 'The key assigned to this request is invalid.',
    anthropicStreamFailed: 'Anthropic stream failed',
    anthropicToolCallLimit: 'Anthropic tool call limit reached',
    irisToolCallLimit: (limit: number) => `Iris stopped after ${limit} consecutive tool rounds.`,
    irisToolRequestInvalid: (error: string) => `Iris received an invalid tool request: ${error}`,
    irisEmptyResponse: 'Iris returned an empty response. Your message was kept so you can try again.',
    claudeBatchResultMissing: 'No results URL returned from Claude batch request',
    claudeBatchTimedOut: 'Claude batch request timed out after 24 hours',
    claudeBatchExpired: 'Claude batch request expired',
    providerOperationFailed: (status: number) => `Provider operation failed (${status})`,
    providerOperationResponseMalformed: 'Provider operation response was malformed',
    imageGenerationFailed: (status: number) => `Image generation failed (${status})`,
    imageGenerationResponseMalformed: 'Image generation response was malformed',
    imageGenerationResponseEmpty: 'Image generation response was empty',
    imageGenerationResponseTooLarge: 'Image generation response exceeded the size limit',
    stableDiffusionNotConfigured: 'Stable diffusion is not set in settings.',
    unexpectedResponseType: 'Unexpected response type',
    referenceImageLoadFailed: 'Reference image failed to load',
    referenceImageLoadFailedWithCause: (error: string) => `Reference image failed to load: ${error}`,
    imageGenerationTimedOut: 'Error: Image generation took longer than expected.',
    openAICompatibleImageUrlMissing: 'OpenAI Compatible API URL is not set',
    waveSpeedApiKeyMissing: 'Please enter wavespeed API key',
    waveSpeedCatalogApiKeyMissing: 'WaveSpeed API Key not set',
    waveSpeedCatalogParseFailed: 'Failed to parse WaveSpeed response',
    waveSpeedCatalogResponseInvalid: 'Invalid WaveSpeed API response',
    waveSpeedCatalogFetchFailed: (error: string) => `Failed to fetch models: ${error}`,
    ttsFailed: (error: string) => `TTS Error: ${error}`,
    gptSoVitsPathLookupFailed: 'Failed to Auto get path',
    fishSpeechModelMissing: 'FishSpeech Model is not selected',
    noData: "The file is invalid, or it's data is corrupted.",
    onlyOneChat: 'There must be least one chat',
    noUserIcon: 'You must set your icon first.',
    emptyText: 'Text is empty.',
    bannedCharacterSet: (script: string) =>
      `The response contained characters from the blocked Unicode script "${script}".`,
    wrongPassword: 'Wrong Password',
    vertexAuthError: 'Vertex AI authentication details are missing.',
    networkFetch: 'This happens when the network is unstable or the server is down.',
    networkFetchWeb:
      'This can be a CORS error caused by browser limitations. Run RisuAI under the Fastify server so requests are routed through the server-side proxy.',
    networkFetchPlain: 'This can be a plain fetch error. try disabling force plain fetch option in settings.',
    requestLogRemoved: 'This request log is removed.',
    requestLogRemovedDesc: 'This request log removes when client is refreshed or reloaded.',
    requestDataMessageMissing: 'The inspected message is no longer present.',
    coldStorageWriteFailed: 'Cold storage write failed. Your chat data has been preserved.',
    coldStorageVerifyFailed: 'Cold storage verification failed. Your chat data has been preserved.',
    coldStorageRecoveryFailed: 'Cold storage recovery failed. The archive pointer was kept.',
    chatGenerationSettingsIncomplete: 'Chat generation settings are incomplete.',
    chatGenerationSettingsIncompleteWithMissing: (missing: string) =>
      `Chat generation settings are incomplete. Missing: ${missing}.`,
  },
  waveSpeedCatalogModelsLoaded: (count: number) => `Successfully loaded ${count} models`,
  showHelp: 'Show Help',
  help: {
    reducedMotion:
      'Minimizes animations and transitions. This app setting is used instead of your operating system or browser preference.',
    hypaV3ProgressOpenChatOnly:
      'Shows the full memory-job indicator only for the open chat. Jobs in other chats remain visible as a compact count.',
    floatingChatInput:
      'Shows a floating chat input when you scroll far enough that the standard input is no longer readily available.',
    model: 'Model option is a main model used in chat.',
    submodel:
      'Auxiliary Model is a model that used in analyzing emotion images and auto suggestions and etc. gpt3.5 is recommended.',
    oaiapikey: 'API key for OpenAI. you can get it in https://platform.openai.com/account/api-keys',
    mainprompt: 'The main prompt option sets the default model behavior.',
    jailbreak: 'The jailbreak prompt option activates when jailbreak toggle is on in character.',
    globalNote: 'A note that strongly affects model behavior, also known as UJB. Works in all characters.',
    autoSuggest: 'Prompts used to generate options when automatically suggesting user responses.',
    formatOrder: 'Formating order of prompt. Lower blocks does more effect to the model.',
    forceUrl: 'If it is not blank, the request will go to the url that you had inputed.',
    tempature:
      "Lower values make character follow prompts closely, but it will more likely to response like a machine.\nHigher values will result in creative behavior, but the character's response can break down more easily.",
    frequencyPenalty:
      "Higher values prevent the use of duplicate words in response, but character's response can break down more easily.",
    presensePenalty:
      "Higher values prevent the use of duplicate words in all context, but character's response can break down more easily.",
    sdProvider: 'Provider for image generation.',
    msgSound: 'Plays *ding* sound when character responses',
    charDesc: 'Brief description of the character. This affects characters response.',
    charFirstMessage: 'First message of the character. This highly affects characters response.',
    customNotificationMessage:
      'When set, completion notifications for this character use this message instead of the default notification text.',
    notificationImage:
      'When set, completion notifications for this character use this image instead of the character icon.',
    charNote: 'A note that strongly affects model behavior. Embbedded to current character, also known as UJB.',
    toggleNsfw: 'Toggles jailbreak prompt on and off.',
    lorebook:
      'Lorebook is a user-made dictionary for AI. AI only sees it when where is an activation keys in the context.',
    loreName: "Name of the lore. It doesn't affect the Ai.",
    agentOnlyLorebook:
      'Reserves this entry for named Agent input. It is excluded from normal lorebook activation and cannot use activation keys or Always Active.',
    loreActivationKey:
      'If one of the activation key exists in context, the lore will be activated and prompt will go in. Separated by commas.',
    loreorder:
      'If insert Order is higher, it will effect the model more, and it is less likely to be cut when many lores are activated.',
    bias: "Bias is a key-value data which modifies the likelihood of string appearing.\nIt can be -100 to 100, higher values will be more likely to appear, and lower values will be more unlikely to appear. \nAdditionaly, if its set to -101, it would work as 'strong ban word' for some models. \nWarning: if the tokenizer is wrong, it not work properly.",
    emotion:
      "Emotion Images option shows image depending at character's emotion which is analyzed by character's response. You must input emotion name as words *(like joy, happy, fear and etc.)*. The emotion named **neutral** will be the default emotion if it exists. There must be more than 3 images to work properly.",
    imggen: 'After analyzing the chat, apply the prompt to {{slot}}.',
    regexScript:
      'Regex Script is a custom regex that replaces string that matches IN to OUT.\n\nThere four type options.' +
      "\n\n- **Modify Input** modifies user's input" +
      "\n\n- **Modify Output** modifies character's output" +
      '\n\n- **Modify Request Data** modifies current chat data when sent.' +
      '\n\n- **Modify Display** just modifies the text when shown without modifying chat data.' +
      '\n\nIN must be a regex without flags and without slashes in start and end.\n\nOUT is a string which can include replacement patterns. These are the patterns:' +
      '\n\n- $$\n\n    - inserts $' +
      '\n\n- $&\n\n    - inserts the matched substring.' +
      '\n\n- $`\n\n    - inserts the portion of the string that precedes the matched substring.' +
      '\n\n- $1\n\n    - inserts the first matching group. Works with other number like 2, 3...' +
      '\n\n- $(name)\n\n    - inserts the named group' +
      '\n\nFor flags, you can not only use native supported flags, but also use these flags, which are designed for advanced users:' +
      '\n\n- `<inject>` - injects the result to the current string.' +
      '\n- `<move_top>` - moves the result to the top of the string.' +
      '\n- `<move_bottom>` - moves the result to the bottom of the string.' +
      '\n- `<repeat_back>` - if the match is not found, it carries the result from the previous match.' +
      '\n- `<order n>` - sets the order of the result. Higher order will be shown first. `n` is a number. (like `<order 1>`) if this flag is not set, it will be set to 0.' +
      '\n- `<cbs>` - parses curly braced synatxes in IN.' +
      '\n\nTo use with native flags, you can use like `gi<cbs><move_top>`.',
    experimental: 'This is a experimental feature. It might be unstable.',
    oogaboogaURL:
      'If your WebUI supports older version of api, your url should look *like https:.../run/textgen*\n\n' +
      'If your WebUI supports newVersion of api, your url should look like *https://.../api/v1/generate* and use the api server as host, and add --api to arguments.',
    exampleMessage:
      "Example conversations that affects output of the character. It doesn't uses tokens permanently." +
      '\n\nExample format of conversations:' +
      '\n\n```\n<START>\n{{user}}: hi\n{{char}}: hello\n<START>\n{{user}}: hi\nHaruhi: hello\n```' +
      '\n\n```<START>``` Marks the beginning of a new conversation.',
    creatorQuotes:
      "Note that appearances on top of first message. Used to inform users about this character. It doesn't go into prompt.",
    systemPrompt: 'A prompt that replaces main prompt in settings if its not blank.',
    chatNote: 'A note that strongly affects model behavior. Embbedded to current chat, also known as memory or UJB.',
    personality:
      "A brief description about character's personality. \n\n**It is not recommended to use this option. Describe it in character description instead.**",
    scenario:
      "A brief description about character's scenario. \n\n**It is not recommended to use this option. Describe it in character description instead.**",
    utilityBot:
      'When activated, it ignores main prompt, jailbreak and other prompts. Used for bot made for utility, not for roleplay.',
    loreSelective:
      'If Selective mode is toggled, both Activation Key and Secondary key should have a match to activate the lore.',
    loreRandomActivation:
      "If Use Probability Condition is enabled, if the lore's other conditions are all met, the lore will be activated with a set probability which is set by 'Probability' each time a chat is sent.",
    additionalAssets:
      'Additional assets to display in your chat. \n\n - use `{{raw::<asset name>}}` to use as path.\n - use `{{image::<asset name>}}` to use as image\n - use `{{video::<asset name>}}` to use as video\n - use `{{audio::<asset name>}}` to use as audio\n    - recommended to put in Background HTML',
    superMemory:
      'SuperMemory makes your character memorize more by giving summarized data to AI.\n\n' +
      'SuperMemory model is a model that summarizes that text. davinci is recommended, and Auxiliary models are not recommended unless it is an unfiltered model with over 2000 tokens with great summarizing skill.\n\n' +
      'SuperMemory Prompt decides what prompt should be sent to summarize. If you leave it blank, it will use the default prompt. leaving blank is recommended.\n\n' +
      'After it is all setup, you can able it in the setting of a character.',
    replaceGlobalNote: "If it's not blank, it replaces current global note to this.",
    backgroundHTML:
      'A Markdown/HTML Data that would be injected to the background of chat screen.\n\n You can also use additional assets. for example, you can use `{{audio::<asset name}}` for background music.' +
      '\n\n Additionaly, you can use these with additional assets:' +
      '\n - `{{bg::<asset name>}}`: inject the background as asset',
    additionalText:
      "The text that would be added to Character Description only when AI thinks it's needed, so you can put long texts here. Separate with double newlines.",
    charjs:
      'A javascript code that would run with character. for example, you can check `https://github.com/kwaroran/Risuai/blob/main/src/etc/example-char.js` CURRENTLY NOT RECOMMENDED FOR USE DUE TO SECURITY REASONS. EXPORTING WOULD NOT INCLUDE THIS.',
    romanizer:
      'Romanizer is a plugin that converts non-roman characters to roman characters to reduce tokens when using non-roman characters while requesting data. This can result different output from the original model. It is not recommended to use this plugin when using roman characters on chat.',
    inlayImages: 'If enabled, images could be inlayed to the chat and AIs can see it if they support it.',
    metrica:
      'Metric Systemizer is a plugin that converts metrics to imperial units when request, and vice versa on output to show user metric system while using imperial for performace. It is not recommended to use this plugin when using imperial units on chat.',
    lorePlus:
      'LoreBook+ is a experimental feature that uses vectordb instead of just string matching for better bot making experience and better matching performace.',
    topP: 'Top P is a probability threshold for nucleus sampling. Model considers the results of the tokens with top_p probability mass.',
    openAIFixer: 'OpenAI Fixer is a plugin that fixes some of the problems of OpenAI.',
    sayNothing: "If enabled, it will input 'say nothing' when no string inputed.",
    showUnrecommended:
      'If enabled, it will show unrecommended, deprecated settings. It is NOT RECOMMENDED to use these settings.',
    showGlobalLorebookAndRegex:
      'Globally enabled modules are the preferred way to provide global lorebooks and regex scripts. These legacy menus are hidden by default.',
    pluginCompatibilityMode:
      'Restores legacy device-local plugin storage APIs in Fastify server mode. Data written there is device-local, unsynced, and excluded from server backup/export. Prefer pluginStorage for durable plugin data.',
    strictScriptCheck:
      'If enabled, server prompt assembly blocks Lua scripts that reference browser dialog APIs before they run. When disabled, those scripts are blocked only if they actually call alertInput, alertSelect, or alertConfirm.',
    complexRegexCompatibilityMode:
      'Strict mode rejects risky nested regex patterns before they run. Compatibility mode runs those patterns in an isolated worker and fails the stage if the configured timeout is exceeded.',
    imageCompression:
      "If enabled, it will compress images when exporting character. if animated images doesn't works, try disabling this option.",
    useExperimental: 'If enabled, it will show some experimental features.',
    forceProxyAsOpenAI: 'If enabled, it will force to use OpenAI format when using reverse proxy.',
    forcePlainFetch:
      'If enabled, it will use browser fetch api instead of native http request. this can cause CORS errors.',
    autoFillRequestURL: 'If enabled, it will autofill request url to match the current model.',
    localNetworkModeDesc:
      'Routes private/LAN model URLs through the Fastify server instead of browser direct fetch.\n\n' +
      '**Purpose**\n' +
      '- Avoid browser private-network/CORS restrictions for `192.168.x.x`, `10.x.x.x`, `localhost`, `.local`, and similar local hosts.\n' +
      '- Mitigate timeout risk for slow first-token local inference behind the Fastify server.\n\n' +
      '**How it works**\n' +
      '- Applies only when Local Network Mode is enabled and the target URL is detected as local/private.\n' +
      '- Streaming uses the `/api/v1/proxy/stream-jobs` relay first; non-streaming uses `/api/v1/proxy/fetch`.\n' +
      '- Browser direct calls to local/private model hosts are blocked by design.\n\n' +
      '**Constraints**\n' +
      '- Scope is OpenAI-compatible request paths only.\n' +
      '- This does not bypass public origin limits between two public domains.\n' +
      '- Use the Fastify-served Risuai URL for this feature to take effect.',
    chainOfThought: 'If enabled, it will add chain of thought prompt to the prompt.',
    gptVisionQuality:
      'This option is used to set the quality of the image detection model. The higher the quality, the more accurate the detection, but more tokens are used.',
    genTimes:
      'This option is used to set the number of responses to generate on support models. Other then first response will be act as cached reroll. This can reduce the cost of the model, but it can also increase the cost if you use it without reroll.',
    requestretrys: 'This option is used to set the number of request retries when request fails.',
    emotionPrompt:
      'This option is used to set the prompt that is used to detect emotion. If it is blank, it will use the default prompt.',
    removePunctuationHypa: 'If enabled, it will remove punctuation before executing HypaMemory.',
    additionalParams:
      'Additional parameters that would be added to the request body. If you want to exclude some parameters, you can put `{{none}}` to the value. If you want to add a header instead of body, you can put `header::` in front of the key like `header::Authorization`. If you want value as json, you can put `json::` in front of the value like `json::{"key":"value"}`. otherwise, type of the value would be determined automatically.',
    antiClaudeOverload:
      'If Claude overload happens, Risuai would try to prevent it by continuing with same prompt, making it less likely to happen. Works only for streamed responses. This could not work for non-official api endpoints.',
    triggerScript:
      'Trigger Script is a custom script that runs when a condition is met. It can be used to modify the chat data, run a command, change variable, and etc. the type depends when it is triggered. It can also be run by buttons, which can be used with {{button::Display::TriggerName}}, or HTML buttons with `risu-trigger="<TriggerName>"` attribute.',
    combineTranslation:
      'If enabled, text that is one sentence but separated by HTML tags will be combined together and translated, then Modify Display script will be reapplied to the translated output.\nThis helps the translator to make the correct translation.\nIf the UI becomes weird when you enable this option, please turn off the option and report it.',
    dynamicAssets:
      'If enabled, if the asset name is not found when processing data, it will try to find the closest asset name by using vector search and replace it with the closest asset name.',
    dynamicAssetsEditDisplay:
      'If enabled, the dynamic assets will be applied to the Modify Display stage too. However, this can cause performance issues.',
    nickname: "Nickname would used be in {{char}} or <char> in chat instead of character's name if it is set.",
    useRegexLorebook:
      'If enabled, it will use regex for lorebook search, instead of string matching. It uses /regex/flags format.',
    customChainOfThought:
      'Warning: chain of thought toggle is no longer recommended to use. Put chain of thought prompt in other prompt entries instead.',
    customPromptTemplateToggle:
      'Here you can define your own prompt toggles. Use `<toggle variable>=<toggle name>` format, seperated by newline. for example, `cot=Toggle COT`. You can use these toggles in prompt by using `{{getglobalvar::toggle_<toggle variable>}}`. like `{{getglobalvar::toggle_cot}}`.',
    defaultVariables:
      "Here you can define your own default variables. Use `<variable name>=<variable value>` format, seperated by newline. For example, `name=Risuai`, which then can be used with trigger scripts and variables CBS like `{{getvar::A}}`, `{{setvar::A::B}}` or `{{? $A + 1}}`. If prompt template's default variable and character's default variable has same name, character's default variable will be used.",
    lowLevelAccess:
      'If enabled, it will enable access to features that requires high computing powers and executing AI model via triggers in the character. Do not enable this unless you really need these features.',
    triggerLLMPrompt:
      'A prompt that would be sent to the model. You can use multi turns and roles by using `@@role user`, `@@role system`, `@@role assistant`. for example, \n```\n@@role system\nrespond as hello\n@@role assistant\nhello\n@@role user\nhi\n```',
    legacyTranslation:
      'If enabled, it will use the old translation method, which preprocess markdown and quotes before translations instead of postprocessing after translations.',
    luaHelp:
      'You can use Lua scripts as a trigger script. You can define onInput, onOutput, onStart functions. onInput is called when user sends a message, onOutput is called when character sends a message, onStart is called when the chat starts. For more information, see the documentation.',
    openAIFlexProcessing:
      'OpenAI Flex responses can be slower than regular responses, but you can get them at a lower price (Batch API pricing). This toggle only applies to official OpenAI Chat Completions requests.',
    claudeCachingExperimental:
      'Caching in Claude is experimental feature that can reduce the cost of the model, but it can also increase the cost if you use it without reroll. Since this is a experimental feature, it can be unstable and behavior can be changed in the future.',
    urllora:
      'You can use direct download link of the model file. You can make direct url from Google Drive like website like https://sites.google.com/site/gdocs2direct/ , or use CivitAI URL, copy the the AIR (looks like `urn:air:flux1:lora:civitai:180891@776656` or just `civitai:180891@776656`) and paste it.',
    v2GetAlertSelect: 'Options are separated by | (pipe) character.',
    v2RegexTest: "Returns 1 if the regex matches, 0 if it doesn't match.",
    v2Calculate:
      'Evaluates mathematical expressions with support for basic arithmetic (+, -, *, /, %, ^), comparison operators (<, >, <=, >=, =, !=), logical operators (&&, ||, !), parentheses for precedence, and variable substitution using $variableName format. Variables are automatically converted to numbers (defaults to 0 if invalid).',
    namespace:
      'Namespace is a unique identifier for the module. It is used to prevent conflicts between modules, and for interaction of presets, other modules and etc. If you are not sure what to put, leave it blank.',
    moduleIntergration:
      'You can enable modules by putting the module namespace in the module intergartion sections. if you want to enable multiple modules, you can seperate them by comma. for example, `module1,module2,module3`. This is for advanced users, who wants to vary the use of modules by presets.',
    customCSS: 'Custom CSS for styling. you can also disable/enable it by pressing (Ctrl + .) if something goes wrong.',
    enableScrollToActiveChar:
      'If enabled, pressing the hotkey or holding Ctrl while dragging a character will scroll to the currently active character. Folders will be opened automatically if closed.',
    unrecommended: 'This is a unrecommended setting. It is not recommended to use this setting.',
    jsonSchema:
      'This is a JSON Schema that will be sent to the AI model if AI model supports JSON Schema.\n\nHowever, since JSON Schema is hard to learn, In Risuai, you can use subset of TypeScript interface instead of JSON Schema. Risuai will convert it in runtime.' +
      'For example, if you want to send a JSON like this:\n\n```js\n{\n  "name": "Risuai", //name must be Risuai,\n  "age": 1, //age must be number,\n  "icon": "slim", //icon must be \'slim\' or \'rounded\'\n  "thoughts": ["Good View!", "Lorem"] //thoughts must be array of strings\n}\n```\n\n' +
      "You can put this TypeScript interface:\n\n```typescript\ninterface Schema {\n  name: string;\n  age: number;\n  icon: 'slim'|'rounded'\n  thoughts: string[]\n}\n```\n\n" +
      "Name of the interface doesn't matter. for more information, see the typescript documentation. (https://www.typescriptlang.org/docs/handbook/interfaces.html), and to Check what subset of TypeScript is supported, see the below." +
      '<details><summary>Supported TypeScript Subset</summary>\n\n' +
      `Supported types are \`boolean\`, \`number\`, \`string\`, \`Array\`. Advanced typing like unit types, intersection types, union types, optional, literal types, and etc. are not supported except for these cases:\n
        - Array of primitive types: (ex. \`string[]\`, \`Array<boolean>)\`
        - Unit types between strings: (ex. \`'slim'|'rounded'\`).

        Properties must be one in a line. if there is multiple properties in a line, it will throw an error. Properties and name of the interface must be only in latin characters, in ASCII range. Name of the properties must not be surrounded by quotes or double quotes. Nesting inside the interface is not supported. It is not allowed to put \`{\` or \`}\` in the line that properties are defined. If you want to use more advanced types, use JSON Schema instead.
        ` +
      '</details>',
    strictJsonSchema:
      'If enabled, it will strictly follow the Provided Schema for JSON on some models. If it is disabled, it may ignore the JSON Schema.',
    extractJson:
      'If it is not blank, it will extract specific JSON data from the response. For example, if you want to extract `response.text[0]` in response `{"response": {"text": ["hello"]}}`, you can put `response.text.0`.',
    translatorNote:
      "Here, you can add a unique translation prompt for each character. This option only applies when using the Ax. model for translation. To apply it, include `{{slot::tnote}}` in the language settings. It doesn't work in group chats.",
    chatHTML:
      'A HTML that would be inserted as each chat.\n\nYou can use CBS and special tags.\n- `<risutextbox>`: a textbox that would be used to render text\n- `<risuicon>`: an icon for user or assistant\n- `<risubuttons>`: icon buttons for chat edit, translations and etc.\n- `<risugeninfo>`: generation information button.',
    systemContentReplacement:
      "The prompt format that replaces system prompt if the model doesn't support system prompt.",
    systemRoleReplacement: "The role that replaces system role if the model doesn't support system role.",
    summarizationPrompt:
      'The prompt that is used for summarization. If it is blank, it will use the default prompt. You can also use ChatML formating with {{slot}} for the chat data.',
    translatorPrompt:
      'The prompt used for translation. If blank, the default prompt is used. ChatML is supported. {{slot}} is the target language, {{slot::from}} the source language, {{slot::tnote}} the translator note, {{slot::content}} the original source in every step, {{slot::prev}} the previous enabled step output (or source in the first step), and {{slot::out::key}} a prior named output.',
    translateBeforeHTMLFormatting:
      'If enabled, it will translate the text before Regex scripts and HTML formatting. This could make the token lesser but could break the formatting.',
    translatorSendTextAsIs:
      "If enabled, Ax. Model translation sends the message text to the model exactly as written — in a single request, with no line protection, splitting, or style placeholders — and the model's response is used verbatim as the translation result.",
    translatorExcludeThoughts:
      'When Send Text As-Is is enabled, removes `<Thoughts>` and `<think>` blocks from the source and translation history before sending them to the model.',
    translatorHistoryMaxTokens:
      'Maximum approximate token budget shared by the source and translated history prompt slots. Whole oldest messages are removed when the limit is exceeded.',
    autoTranslateCachedOnly:
      "If enabled, chats with Auto Translation on display stored translations but don't request new LLM translations.",
    presetChain:
      'If it is not blank, the preset will be changed and applied randomly every time when user sends a message in the preset list in this input. Preset list should be seperated by comma, for example, `preset1,preset2`.',
    agentPresetPreparedInputs:
      'Prepared inputs are context sections collected for this Agent only when the selected source is inserted with its shown CBS placeholder, such as `{{currentUserMessage}}`. Put the placeholder wherever that content should appear in the Agent instruction. Max input chars still limits collected content. Main draft is only available when the Agent is used after the main response.',
    legacyMediaFindings:
      'If enabled, it will use the old method to find media assets, without using the additional search algorithm.',
    comfyWorkflow:
      "Put the API workflow of comfy UI. you can get your API workflow in comfy UI by pressing the 'Workflow > Export (API)' button. you must also put {{risu_prompt}} in you workflow text. The {{risu_prompt}} will be replaced with the prompt provided by the Risu.",
    automaticCachePoint: "Automatically creates cache point after the chat ends, if the caching point doesn't exist.",
    experimentalChatCompressionDesc:
      'Compresses the unused chat data and saves in seperate file. This greatly reduces the size of the chat data, and greatly improves the performance, however its experimental and can be unstable, causing issues in backup feature and more.',
    promptInfoInsideChatDesc:
      'When enabled, this stores prompt preset information in the chat metadata. The stored data includes the preset name, active toggles, and the prompt text. This may slightly increase processing time and storage usage.',
    autoAdjustSchema: 'When enabled, it will automatically adjust the JSON schema for Dynamic Output.',
    dynamicMessages:
      'When enabled, it will allow the assistant to send multiple messages in a row, instead of one at a time.',
    dynamicMemory:
      'When enabled, assistant will make memory notes on response time. Additional prompting is required to utilize this feature.',
    dynamicResponseTiming: 'When enabled, it will adjust the response timing dynamically.',
    dynamicRequest: 'When enabled, it will request to model at random timing without waiting for user input.',
    settingsCloseButtonSize:
      'Adjusts the size of the close (X) button in the top right corner of the settings window. Default is 24.',
    chatDisplayTailCount:
      'Controls how many latest chat messages are rendered and hydrated when opening a chat. Lower values can make large chats open faster. Default is 30.',
    chatLoadInitialPages:
      'Number of recent chat messages to render when a chat screen opens. Higher values show more history immediately but can make long chats heavier to open.',
    chatLoadAdditionalPages:
      'Number of older chat messages to render each time you scroll to the top. Higher values reduce repeated loading but can make each load heavier.',
    showTypingEffect: 'When enabled, it will show a typing indicator while the assistant is generating a response.',
    dynamicOutputPrompt: 'When enabled, the schema information will be included in the request.',
    openRouterProviderOrder:
      'The order of providers to use, the first provider will be used first, if the provider is not available, it will use the next provider. See datail on https://openrouter.ai/docs/guides/routing/provider-selection#ordering-specific-providers',
    openRouterProviderOnly:
      'Only use the providers in this list, if all the provider is not available, the request will failed. See detail on https://openrouter.ai/docs/guides/routing/provider-selection#allowing-only-specific-providers',
    openRouterProviderIgnore:
      'Ignore the providers in this list, if all the provider is ingored, the request will failed. See detail on https://openrouter.ai/docs/guides/routing/provider-selection#ignoring-providers',
    additionalPrompt:
      "Text that gets appended to the Main Prompt when Prompt Preprocess is enabled. Default is 'The assistant must act as {{char}}. user is {{user}}.' This helps set up basic roleplay context.",
    hideAllImagesDesc: 'Hides bot icons, bot image assets, and card cover images.',
    embedding:
      'Embedding model is used for similarity search across multiple features:\n\n' +
      '- **Long Term Memory**: HypaV2, HypaV3, Hanurai Memory, and SupaMemory (with HypaMemory enabled)\n' +
      '- **Additional Text**: Matching character additional info based on context\n' +
      '- **Dynamic Assets**: Finding similar asset names when exact match is not found\n' +
      '- **Trigger Scripts**: Similarity conditions in trigger scripts\n' +
      '- **File Attachments**: Searching within PDF/TXT/XML attachments',
    keepSessionAlive:
      'Keeps the tab active and prevents the session from expiring due to inactivity in browsers. This may require refresh to take effect.\n\n' +
      '- **Via Sound**: Continuously plays nearly silent audio to keep the session alive. Because this keeps the browser audio session in use, it may reduce, pause, or otherwise interfere with audio from other applications. Use it only when needed.\n',
    reSummarizationPrompt:
      'The prompt used when merging multiple selected summaries into one via bulk edit. If blank, the default prompt is used.',
    hypaV3MemoryTokensRatio:
      'The fraction of the max context size allocated to the long-term memory block {{slot}} in the prompt.',
    hypaV3ExtraSummarizationRatio:
      'Lowers the threshold at which summarization stops. At 0, summarization stops as soon as tokens fall below the max context. Higher values cause more summarization before stopping.',
    hypaV3MaxChatsPerSummary: 'Maximum number of chat messages to include when creating a single summary.',
    hypaV3RecentMemoryRatio:
      'The fraction of memory tokens allocated to recent memory. Automatically filled with the most recently created summaries until the allocated tokens are full.',
    hypaV3SimilarMemoryRatio:
      'The fraction of memory tokens allocated to similar memory. Automatically filled with summaries that have the highest similarity scores to recent chats until the allocated tokens are full.',
    hypaV3RandomMemoryRatio: 'Randomly filled from summaries not already selected by other categories.',
    hypaV3PreserveOrphanedMemory:
      'If enabled, summaries that reference deleted chat messages will be preserved. If disabled, summaries whose source messages no longer exist are automatically removed.',
    hypaV3ProcessRegexScript:
      'If enabled, regex scripts will be applied to the input chat messages when regenerating summaries in the HypaV3 modal.',
    hypaV3DoNotSummarizeUserMessage: 'If enabled, user messages are excluded from the max messages per summary count.',
    hypaV3EnableSimilarityCorrection:
      'If enabled, a summary of recent chats is additionally used as a query. Does not work with the experimental HypaMemory V3.',
    hypaV3UseExperimentalImpl:
      'Switches to the experimental HypaMemory V3 implementation. Enables rate limit settings and changes the query method.',
    hypaV3AlwaysToggleOn: 'If enabled, the HypaMemory toggle is automatically activated when selecting a character.',
    hypaV3SummarizationRequestsPerMinute:
      'Maximum SuperMemory model requests per minute for summarization. Only applies when the SuperMemory model is set to Auxiliary Model.',
    hypaV3SummarizationMaxConcurrent:
      'Maximum concurrent SuperMemory model requests for summarization. Only applies when the SuperMemory model is set to Auxiliary Model.',
    hypaV3EmbeddingRequestsPerMinute: 'Maximum embedding model requests per minute for similarity search.',
    hypaV3EmbeddingMaxConcurrent: 'Maximum concurrent embedding model requests for similarity search.',
    hypaV3QueryChatCount:
      'The number of recent chat messages used as the query for similarity search. ' +
      'Higher values use more chat context to determine similarity.',
    hypaV3SummaryChunkSeparator: 'Separator used to split summaries into chunks for similarity search.',
    coldstorage:
      'Coldstorage is a feature that automatically moves old chats and character data to a seperate storage to reduce the size of the main storage and improve performance. This will reduce the transfer time, transfer traffic and improve the performance when loading chats.',
  },
  setup: {
    chooseProvider: 'Choose AI Provider',
    openaikey: 'OpenAI with API Key (Recommended)',
    openaiProxy: 'OpenAI Reverse proxy',
    setupmodelself: 'Others / I will setup myself',
    inputApiKey: 'Input API Key Here',
    apiKeyhelp: 'You can get API key from: ',
    setupSelfHelp: 'Setup yourself in settings, after Welcome screen ends.',
    theme: 'Select your theme',
    themeDescWifulike: 'Not suitable for mobile',
    themeDescWifuCut: 'Suitable for mobile',
    themeDescClassic: 'Suitable for All devices',
    texttheme: 'Select your text color',
    inputName: 'Lastly, input your Nickname.',
    welcome:
      'Welcome to Risuai! I am Airisu, I am here to guide you through the Risuai setup. First, what may I call you?',
    welcome2:
      'Hello {username}! Before we start, I will ask you some questions. You can change these settings later in settings.\n\nFirst select the AI provider.',
    openRouterProvider:
      'OpenRouter has a lot of models, some of them unfiltered and some of them free, but it is not as good as OpenAI.',
    hordeProvider: 'Horde is a free provider, but the response time is long and the quality is low.',
    setProviderLater:
      'There are other providers, but you can set it later in settings. Select this if you want to set it later.',
    setupOpenAI:
      "To use OpenAI, you need to get an API key. \n1. Go to https://beta.openai.com/ \n2. Login with your account \n3. Go to https://beta.openai.com/account/api-keys \n4. Click 'Create New API Key' and name your key whatever you want. \n5. Copy the key in the website.\n6. Go back to Risuai\n7. Paste it, and click send button.",
    setupClaude: 'To use Claude, you need to get an API key.',
    setupClaudeSteps: [
      'Go to this URL and Login as Google',
      "Input your information and click 'Continue'",
      "On organization page, just input any name only at the first box, and click 'Create Account'",
      "Press 'Buy Credits' button",
      "Press 'Complete Setup' button",
      "Input just like the image above, except the country section, which you need to input your country. then press 'Continue'",
      "Add your payment method and press 'Continue'",
      "After payment has been registered, press 'Add Funds' and add funds. you can add minimum 5$, or you can setup auto add reload.",
      "After adding funds, go to the dashboard page and press 'Get API Keys' button",
      'Press "Create API Key" button',
      "Input just like the image above, and press 'Add'",
      'Copy the key, and paste it in here and send it.',
    ],
    setupOpenRouter:
      "To use OpenRouter, you need to get an API key. \n1. Go to https://openrouter.ai/keys\n2. Click 'Create Key'\n3. Set key name whatever you want.\n4. Copy the key in the website\n5. Go back to Risuai\n6. Paste it, and click send button.",
    allDone: 'All Done! Please wait a moment.',
    setupLaterMessage: 'Welcome {username}! Do you want me to guide you to setup or do it yourself?',
    setupMessageOption1: 'Guide me to setup',
    setupMessageOption1Desc: 'Recommended for new users',
    setupMessageOption2: 'I will setup myself',
    claudeDesc:
      "Claude is a AI model made by Antropic, competitor to OpenAI. It's better than GPT if you want to use it in other languages than English.",
    openAIDesc: 'OpenAI GPT is a high quality AI model, but it is paid and filtered, and weak in other than English.',
    chooseChatType: 'Great! Now select the chat language.',
    chooseChatTypeOption1: 'Full English',
    chooseChatTypeOption1Desc:
      'This will make AI to use English and input/output in English. Recommended for English speakers.',
    chooseChatTypeOption2: 'English Processing',
    chooseChatTypeOption2Desc:
      'This will make AI to use English internaly, but input/output will be in your language. Recommended for non-English speakers.',
    chooseChatTypeOption3: 'No Language Focused',
    chooseChatTypeOption3Desc: 'This will make AI to use your language internaly, but this could make quality lower.',
    chooseCheapOrMemory: 'By the way, do you prefer memory or cheapness?',
    chooseCheapOrMemoryOption1: 'Memory',
    chooseCheapOrMemoryOption1Desc: 'This will make AI to remember more, but it will quite expensive.',
    chooseCheapOrMemoryOption2: 'Cheap',
    chooseCheapOrMemoryOption2Desc: 'This will make AI to remember less, but it will be cheaper.',
    chooseCheapOrMemoryOption3: 'Balanced',
    chooseCheapOrMemoryOption3Desc: 'This will make AI to remember more than cheap, but less than memory.',
    chooseCheapOrMemoryOption4: 'Unlimited',
    chooseCheapOrMemoryOption4Desc: 'This will make AI to remember almost everything, but it will be very expensive.',
    finally: 'Finally, do you want to enable advanced tools?',
    finallyOption1: 'Yes',
    finallyOption1Desc:
      'This will enable advanced tools, but it will make the UI more complex. Recommended for advanced users.',
    finallyOption2: 'No',
    finallyOption2Desc: 'This will disable advanced tools, and make the UI more simple. Recommended for new users.',
    igpPrompt:
      'if IGP Prompt is not a blank, it will be executed after the main model request, after main model execution, as a emotion model, adding the result after the main model response.',
  },
  triggerCategories: {
    Special: 'Special',
    Control: 'Control',
    Chat: 'Chat',
    'Low Level': 'Low Level',
    Alert: 'Alert',
    'Lorebook V2': 'Lorebook V2',
    String: 'String',
    Data: 'Data',
    Array: 'Array',
    Others: 'Others',
    Deprecated: 'Deprecated',
  },
  triggerDesc: {
    v2Header: 'Header',
    v2HeaderDesc: 'Header',
    v2If: 'If',
    v2IfDesc: 'If {{source}} {{condition}} {{target}}',
    v2Else: 'Else',
    v2ElseDesc: 'Else',
    v2EndIndent: 'End Indent',
    v2EndIndentDesc: 'End Indent',
    v2SetVar: 'Set Variable',
    v2SetVarDesc: 'Set Variable {{var}} {{operator}} {{value}}',
    v2Loop: 'Loop Forever',
    v2LoopDesc: 'Loop Forever',
    v2BreakLoop: 'Break Loop',
    v2BreakLoopDesc: 'Break Loop',
    v2RunTrigger: 'Run Trigger',
    v2RunTriggerDesc: 'Run Trigger {{target}}',
    v2ConsoleLog: 'Console Log',
    v2ConsoleLogDesc: 'Console Log {{source}}',
    v2StopTrigger: 'Stop Trigger',
    v2StopTriggerDesc: 'Stop Trigger',
    v2CutChat: 'Cut Chat',
    v2CutChatDesc: 'Cut Chat from {{start}} to {{end}}',
    v2ModifyChat: 'Modify Chat',
    v2ModifyChatDesc: 'Modify Chat at index {{index}} with {{value}}',
    v2SystemPrompt: 'Add System Prompt',
    v2SystemPromptDesc: 'Add System Prompt at {{location}} with {{value}}',
    v2Impersonate: 'Send Chat',
    v2ImpersonateDesc: 'Send Chat as {{role}} with {{value}}',
    v2Command: 'Run Command',
    v2CommandDesc: 'Run command with {{value}}',
    v2SendAIprompt: 'Send AI Prompt',
    v2SendAIpromptDesc: 'Send AI Prompt',
    v2ImgGen: 'Image Generation',
    v2ImgGenDesc: 'Generate image from {{value}}, negative from {{negValue}} => {{outputVar}}',
    v2CheckSimilarity: 'Check Similarity',
    v2CheckSimilarityDesc: 'Check similarity between {{source}} and {{value}} => {{outputVar}}',
    v2RunLLM: 'Run Model',
    v2RunLLMDesc: 'Send request to model from {{value}} => {{outputVar}}',
    v2ShowAlert: 'Show Alert',
    v2ShowAlertDesc: 'Show alert with {{value}}',
    v2ExtractRegex: 'Extract Regex',
    v2ExtractRegexDesc:
      'Extract text from {{value}} with regex {{regexType}} {{regex}} and flags {{flagsType}} {{flags}}, then store result as {{resultType}} {{result}} => {{outputVar}}',
    v2RegexTest: 'Regex Test',
    v2RegexTestDesc: 'Test {{value}} with regex {{regex}} and flags {{flags}} => {{outputVar}}',
    v2GetLastMessage: 'Get Last Message',
    v2GetLastMessageDesc: 'Get Last Message => {{outputVar}}',
    v2GetMessageAtIndex: 'Get Message at Index',
    v2GetMessageAtIndexDesc: 'Get Message at Index {{index}} => {{outputVar}}',
    v2GetMessageCount: 'Get Message Count',
    v2GetMessageCountDesc: 'Get Message Count => {{outputVar}}',
    v2GetLorebook: 'Get Lorebook',
    v2GetLorebookDesc: 'Get Lorebook named {{target}} => {{outputVar}}',
    v2GetLorebookCount: 'Get Lorebook Count',
    v2GetLorebookCountDesc: 'Get Lorebook Count => {{outputVar}}',
    v2GetLorebookEntry: 'Get Lorebook with index',
    v2GetLorebookEntryDesc: 'Get Lorebook with index {{index}} => {{outputVar}}',
    v2SetLorebookActivation: 'Set Lorebook Activation',
    v2SetLorebookActivationDesc: "Set Lorebook with index {{index}}'s activation state to {{value}}",
    v2GetLorebookIndexViaName: 'Get Lorebook Index via Name',
    v2GetLorebookIndexViaNameDesc: 'Get Lorebook Index via Name {{name}} => {{outputVar}}',
    v2ModifyLorebook: 'Modify Lorebook',
    v2ModifyLorebookDesc: 'Modify Lorebook named {{target}} with {{value}}',
    v2LoopNTimes: 'Loop N Times',
    v2LoopNTimesDesc: 'Loop {{value}} Times',
    v2Random: 'Random',
    v2RandomDesc: 'Random from {{min}} to {{max}} => {{outputVar}}',
    v2GetCharAt: 'Get Character at Index',
    v2GetCharAtDesc: 'Get Character at Index {{index}} from {{source}} => {{outputVar}}',
    v2GetCharCount: 'Get Character Count',
    v2GetCharCountDesc: 'Get Character Count from {{source}} => {{outputVar}}',
    v2ToLowerCase: 'Convert to Lower Case',
    v2ToLowerCaseDesc: 'Convert {{source}} to Lower Case => {{outputVar}}',
    v2ToUpperCase: 'Convert to Upper Case',
    v2ToUpperCaseDesc: 'Convert {{source}} to Upper Case => {{outputVar}}',
    v2SetCharAt: 'Set Character at Index',
    v2SetCharAtDesc: 'Set Character at Index {{index}} in {{source}} to {{value}} => {{outputVar}}',
    v2SplitString: 'Split String',
    v2SplitStringDesc: 'Split {{source}} by {{delimiter}} => {{outputVar}}',
    v2GetCharacterDesc: 'Get Character Description',
    v2GetCharacterDescDesc: 'Get Character Description => {{outputVar}}',
    v2SetCharacterDesc: 'Set Character Description',
    v2SetCharacterDescDesc: 'Set Character Description to {{value}}',
    v2GetPersonaDesc: 'Get Persona Prompt',
    v2GetPersonaDescDesc: 'Get Persona Prompt => {{outputVar}}',
    v2SetPersonaDesc: 'Set Persona Prompt',
    v2SetPersonaDescDesc: 'Set Persona Prompt to {{value}}',
    v2GetReplaceGlobalNote: 'Get Replace Global Note',
    v2GetReplaceGlobalNoteDesc: 'Get Replace Global Note => {{outputVar}}',
    v2SetReplaceGlobalNote: 'Set Replace Global Note',
    v2SetReplaceGlobalNoteDesc: 'Set Replace Global Note to {{value}}',
    v2GetAuthorNote: 'Get Author Note',
    v2GetAuthorNoteDesc: 'Get Author Note => {{outputVar}}',
    v2SetAuthorNote: 'Set Author Note',
    v2SetAuthorNoteDesc: 'Set Author Note to {{value}}',
    v2Comment: 'Add Comment',
    v2CommentDesc: '{{value}}',
    v2DeclareLocalVar: 'Declare Local Variable',
    v2DeclareLocalVarDesc: 'Declare local variable {{var}} with value {{value}}',
    v2MakeArrayVar: 'Make Array Variable',
    v2MakeArrayVarDesc: 'Make Array Variable {{var}}',
    v2GetArrayVarLength: 'Get Array Variable Length',
    v2GetArrayVarLengthDesc: 'Get Length of Array Variable {{var}} => {{outputVar}}',
    v2GetArrayVar: 'Get Array Variable',
    v2GetArrayVarDesc: 'Get Value at Index {{index}} from Array Variable {{var}} => {{outputVar}}',
    v2SetArrayVar: 'Set Array Variable',
    v2SetArrayVarDesc: 'Set Value at Index {{index}} in Array Variable {{var}} to {{value}}',
    v2PushArrayVar: 'Add to Array Variable',
    v2PushArrayVarDesc: 'Add {{value}} to Array Variable {{var}}',
    v2PopArrayVar: 'Remove last value from Array Variable and get',
    v2PopArrayVarDesc: 'Remove last value from Array Variable {{var}} => {{outputVar}}',
    v2ShiftArrayVar: 'Remove first value from from Array Variable and get',
    v2ShiftArrayVarDesc: 'Remove first value from Array Variable {{var}} => {{outputVar}}',
    v2UnshiftArrayVar: 'Add to Array Variable as first value',
    v2UnshiftArrayVarDesc: 'Add {{value}} as first value of Array Variable {{var}}',
    v2SpliceArrayVar: 'Add to Array Variable in Index',
    v2SpliceArrayVarDesc: 'Add {{item}} as {{start}} value of Array Variable {{var}}',
    v2SliceArrayVar: 'Slice Array Variable',
    v2SliceArrayVarDesc: 'Slice Array Variable {{var}} from {{start}} to {{end}} => {{outputVar}}',
    v2GetIndexOfValueInArrayVar: 'Get Index of Value in Array Variable',
    v2GetIndexOfValueInArrayVarDesc: 'Get Index of {{value}} in Array Variable {{var}} => {{outputVar}}',
    v2RemoveIndexFromArrayVar: 'Remove Index from Array Variable',
    v2RemoveIndexFromArrayVarDesc: 'Remove Value at Index {{index}} from Array Variable {{var}}',
    v2JoinArrayVar: 'Join Array',
    v2JoinArrayVarDesc: 'Join Array {{var}} with {{delimiter}} => {{outputVar}}',
    v2MakeDictVar: 'Make Dictionary Variable',
    v2MakeDictVarDesc: 'Make Dictionary Variable {{var}}',
    v2GetDictVar: 'Get Dictionary Value',
    v2GetDictVarDesc: 'Get Value for Key {{key}} from Dictionary {{var}} => {{outputVar}}',
    v2SetDictVar: 'Set Dictionary Value',
    v2SetDictVarDesc: 'Set Value for Key {{key}} in Dictionary {{var}} to {{value}}',
    v2DeleteDictKey: 'Delete Dictionary Key',
    v2DeleteDictKeyDesc: 'Delete Key {{key}} from Dictionary {{var}}',
    v2HasDictKey: 'Has Dictionary Key',
    v2HasDictKeyDesc: 'Check if Dictionary {{var}} has Key {{key}} => {{outputVar}}',
    v2ClearDict: 'Clear Dictionary',
    v2ClearDictDesc: 'Clear Dictionary {{var}}',
    v2GetDictSize: 'Get Dictionary Size',
    v2GetDictSizeDesc: 'Get Size of Dictionary {{var}} => {{outputVar}}',
    v2GetDictKeys: 'Get Dictionary Keys',
    v2GetDictKeysDesc: 'Get All Keys from Dictionary {{var}} => {{outputVar}}',
    v2GetDictValues: 'Get Dictionary Values',
    v2GetDictValuesDesc: 'Get All Values from Dictionary {{var}} => {{outputVar}}',
    v2ConcatString: 'Concatenate Strings',
    v2ConcatStringDesc: 'Concatenate {{source1}} and {{source2}} => {{outputVar}}',
    v2GetLastUserMessage: 'Get Last User Message',
    v2GetLastUserMessageDesc: 'Get Last User Message => {{outputVar}}',
    v2GetLastCharMessage: 'Get Last Character Message',
    v2GetLastCharMessageDesc: 'Get Last Character Message => {{outputVar}}',
    v2GetFirstMessage: 'Get First Message',
    v2GetFirstMessageDesc: 'Get First Message => {{outputVar}}',
    v2GetAlertInput: 'Get Alert Input',
    v2GetAlertInputDesc: 'Get Alert Input => {{outputVar}}',
    v2GetAlertSelect: 'Get Alert Select',
    v2GetAlertSelectDesc: 'Get Alert Select {{display}} options {{value}} => {{outputVar}}',
    v2UnsupportedTrigger: 'Unsupported Trigger',
    v2UnsupportedTriggerDesc: 'Unsupported Trigger',
    v2GetDisplayState: 'Get Display State',
    v2GetDisplayStateDesc: 'Get Display State => {{outputVar}}',
    v2SetDisplayState: 'Set Display State',
    v2SetDisplayStateDesc: 'Set Display State to {{value}}',
    v2UpdateGUI: 'Update GUI',
    v2UpdateGUIDesc: 'Update GUI',
    v2UpdateChatAt: 'Update Chat At Index',
    v2UpdateChatAtDesc: 'Redraw chat at {{index}}',
    v2Wait: 'Wait',
    v2WaitDesc: 'Wait {{value}} seconds',
    v2GetRequestState: 'Get Request Data Content',
    v2GetRequestStateDesc: 'Get Request Content at {{index}} => {{outputVar}}',
    v2SetRequestState: 'Set Request Data Content',
    v2SetRequestStateDesc: 'Set Request Content at {{index}} to {{value}}',
    v2GetRequestStateRole: 'Get Request Data Role',
    v2GetRequestStateRoleDesc: 'Get Request Data Role at {{index}} => {{outputVar}}',
    v2SetRequestStateRole: 'Set Request Data Role',
    v2SetRequestStateRoleDesc: 'Set Request Data Role at {{index}} to {{value}}',
    v2GetRequestStateLength: 'Get Request Data Length',
    v2GetRequestStateLengthDesc: 'Get Request Data Length => {{outputVar}}',
    v2IfAdvanced: 'If',
    v2IfAdvancedDesc: 'If {{source}} {{condition}} {{target}}',
    v2QuickSearchChat: 'Quick Search Chat',
    v2QuickSearchChatDesc:
      'Quick search {{value}} in chat with {{condition}} condition in {{depth}} depth => {{outputVar}}',
    v2StopPromptSending: 'Stop Prompt Sending',
    v2StopPromptSendingDesc: 'Stop Prompt Sending',
    v2Tokenize: 'Tokenize',
    v2TokenizeDesc: 'Tokenize {{value}} => {{outputVar}}',
    v2GetAllLorebooks: 'Get All Lorebooks',
    v2GetAllLorebooksDesc: 'Get all lorebook prompts as array => {{outputVar}}',
    v2GetLorebookByName: 'Get Lorebook Indices by Name',
    v2GetLorebookByNameDesc: 'Get lorebook indices array by name {{name}} => {{outputVar}}',
    v2GetLorebookByIndex: 'Get Lorebook',
    v2GetLorebookByIndexDesc: 'Get lorebook prompt at index {{index}} => {{outputVar}}',
    v2CreateLorebook: 'Create Lorebook',
    v2CreateLorebookDesc: 'Create lorebook with name {{name}}, key {{key}}, content {{content}}',
    v2ModifyLorebookByIndex: 'Modify Lorebook',
    v2ModifyLorebookByIndexDesc:
      'Modify lorebook at index {{index}} with name {{name}}, key {{key}}, content {{content}}',
    v2DeleteLorebookByIndex: 'Delete Lorebook',
    v2DeleteLorebookByIndexDesc: 'Delete lorebook at index {{index}}',
    v2GetLorebookCountNew: 'Get Lorebook Count',
    v2GetLorebookCountNewDesc: 'Get total lorebook count => {{outputVar}}',
    v2SetLorebookAlwaysActive: 'Set Lorebook Always Active',
    v2SetLorebookAlwaysActiveDesc: 'Set lorebook at index {{index}} always active to {{value}}',
    v2Calculate: 'Calculate',
    v2CalculateDesc: 'Calculate {{expression}} => {{outputVar}}',
    v2ReplaceString: 'Replace String',
    v2ReplaceStringDesc: 'Replace {{source}} with regex {{regex}} to {{replacement}} => {{outputVar}}',
  },
  triggerInputLabels: {
    value: 'Value',
    var: 'Variable',
    varName: 'Variable Name',
    outputVar: 'Output Variable',
    name: 'Name',
    index: 'Index',
    source: 'Source',
    target: 'Target',
    condition: 'Condition',
    operator: 'Operator',
    role: 'Role',
    location: 'Location',
    prompt: 'Prompt',
    model: 'Model',
    start: 'Start',
    end: 'End',
    min: 'Min',
    max: 'Max',
    delimiter: 'Delimiter',
    depth: 'Depth',
    key: 'Key',
    alertContent: 'Alert Content',
    options: 'Options',
    cmd: 'Command',
    regex: 'Regex',
    replacement: 'Replacement',
    flags: 'Flags',
    negPrompt: 'Negative Prompt',
    resultFormat: 'Result Format',
    activationKeys: 'Activation Keys',
    insertOrder: 'Insert Order',
    sysStart: 'System Start',
    sysHistoryEnd: 'System History End',
    sysPromptEnd: 'System Prompt End',
    addElse: 'Add Else',
    alwaysActive: 'Always Active',
    trigger: 'Trigger',
    triggerOn: 'Trigger On',
    valueTypeDesc: 'Use directly input value',
    varTypeDesc: 'Use value stored in variable',
    conditionEqual: 'Equal (=)',
    conditionNotEqual: 'Not Equal (≠)',
    conditionGreater: 'Greater (>)',
    conditionLess: 'Less (<)',
    conditionGreaterEqual: 'Greater or Equal (≥)',
    conditionLessEqual: 'Less or Equal (≤)',
    conditionSimilar: 'Similar (≒)',
    conditionContains: 'Contains (∋)',
    conditionIn: 'In (∈)',
    conditionNotContains: 'Not Contains (∌)',
    conditionNotIn: 'Not In (∉)',
    conditionTruthy: 'Truthy (≡)',
    operatorSet: 'Set (=)',
    operatorAdd: 'Add (+=)',
    operatorSubtract: 'Subtract (-=)',
    operatorMultiply: 'Multiply (*=)',
    operatorDivide: 'Divide (/=)',
    operatorModulo: 'Modulo (%=)',
    searchLoose: 'Loose Search',
    searchStrict: 'Strict Search',
    searchRegex: 'Regex Search',
    modelMain: 'Main Model',
    modelSub: 'Sub Model',
    roleUser: 'User',
    roleChar: 'Character',
    roleSystem: 'System',
    roleAssistant: 'Assistant',
    boolTrue: 'True',
    boolFalse: 'False',
    boolNull: 'Null',
    helpVarName: 'Enter the name of the variable',
    helpOutputVar: 'Enter the variable name to store the result',
    helpIndex: 'Array or message index number',
    helpRegex: 'Enter regex pattern',
    helpFlags: 'Regex flags',
    helpDelimiter: 'Delimiter to split the string',
    helpActivationKeys: 'Comma-separated activation keywords',
    helpInsertOrder: 'Insert order',
    helpDepth: 'Depth of messages to search',
    helpMinMax: 'Min and max value range',
    helpSlot: 'Use {{slot}} to keep unchanged',
    expression: 'Expression',
  },
  translationRunFailed: (error: string) => `Translation failed: ${error}`,
  confirm: 'Confirm',
  goback: 'Go Back',
  botSettings: 'Bot Settings',
  model: 'Model',
  apiKey: 'API Key',
  providerURL: 'Request URL',
  providerJSON: 'Request Body JSON',
  mainPrompt: 'Main Prompt',
  jailbreakPrompt: 'Jailbreak Prompt',
  globalNote: 'Global Note',
  autoSuggest: 'Auto Suggest',
  tokens: 'Tokens',
  maxContextSize: 'Max Context Size',
  maxResponseSize: 'Max Response Size',
  temperature: 'Temperature',
  frequencyPenalty: 'Frequency Penalty',
  presensePenalty: 'Presense Penalty',
  advancedSettings: 'Advanced Settings',
  advancedSettingsWarn: "Warn: If you don't know what the option does, don't change it!",
  formatingOrder: 'Formatting Order',
  authorNote: "Author's Note",
  firstMessage: 'First Message',
  customNotificationMessage: 'Custom Notification Message',
  notificationImage: 'Notification Image',
  displayName: 'Display Name',
  description: 'Description',
  note: 'Note',
  jailbreakToggle: 'Toggle Jailbreak',
  chatGenerationPresetUnconfigured: 'Select chat preset',
  chatGenerationModelPresetUnconfigured: 'Select model preset',
  chatGenerationModelPresetRecommendationMismatch: (recommendedModelPresetName: string) =>
    `The selected model preset differs from the prompt's recommended preset: ${recommendedModelPresetName}`,
  chatGenerationPromptPresetUnconfigured: 'Select prompt preset',
  chatGenerationPersonaUnconfigured: 'Select chat persona',
  chatGenerationResetDefaults: 'Reset toggle defaults',
  chatGenerationResetDefaultsConfirm: 'Are you sure you want to reset toggle defaults?',
  chatGenerationTogglePresetCaption: 'Toggle preset',
  chatGenerationTogglePresetSelect: 'Select saved toggles',
  chatGenerationTogglePresetSave: 'Save',
  chatGenerationTogglePresetApply: 'Apply',
  chatGenerationTogglePresetDelete: 'Delete',
  chatGenerationTogglePresetNamePrompt: 'Name this toggle preset',
  chatGenerationTogglePresetSaveChoice: (name: string) => `Save changes to "${name}"?`,
  chatGenerationTogglePresetOverwrite: 'Overwrite it',
  chatGenerationTogglePresetCreateNew: 'Create new Saved Toggle',
  chatGenerationTogglePresetApplyConfirm: 'Apply these saved toggles to the current chat?',
  chatGenerationTogglePresetTypeMismatchWarning:
    'The selected saved toggles do not match the toggles available in this chat.',
  chatGenerationTogglePresetDeleteConfirm: (name: string) => `Delete "${name}"?`,
  chatGenerationTogglePresetUnused: 'Unused',
  chatGenerationTogglePresetUnlinked: 'Unlinked',
  chatGenerationTogglePresetMismatch: 'Mismatch',
  chatGenerationTogglePresetEdited: (name: string) => `${name} (Edited)`,
  chatGenerationTogglePresetDialogTitle: 'Saved Toggles',
  chatGenerationTogglePresetUnselect: 'Unselect',
  chatGenerationTogglePresetPick: 'Pick',
  chatGenerationTogglePresetPickSourceTitle: 'Choose a toggle source',
  chatGenerationTogglePresetPickPresetTitle: (name: string) => `Pick values for ${name}`,
  chatGenerationTogglePresetPickPromptSource: (name: string) => `Prompt preset: ${name}`,
  chatGenerationTogglePresetPickModuleSource: (name: string) => `Module: ${name}`,
  chatGenerationTogglePresetPickAgentSource: (name: string) => `Agent: ${name}`,
  chatGenerationTogglePresetPickMissingKeys: (count: number) => `Missing ${count} required key(s)`,
  chatGenerationTogglePresetPickKindMismatch: (count: number) => `${count} toggle kind mismatch(es)`,
  chatGenerationTogglePresetPickConfirm: (name: string, count: number) =>
    `Copy ${count} changed value(s) for ${name} into this chat?`,
  chatGenerationTogglePresetMismatchOverwriteConfirm:
    'This will replace the saved toggle preset with a materially different toggle structure, not merely update its values. Continue?',
  chatGenerationTogglePresetNoPresets: 'No saved toggle presets.',
  chatGenerationTogglePresetNoSources: 'No active toggle sources are available.',
  chatGenerationTogglePresetBack: 'Back',
  charIcon: 'Character Icon',
  characterDisplay: 'Character Display',
  viewScreen: 'Additional Character Screen',
  none: 'None',
  emotionImage: 'Emotion Images',
  noImages: 'No Images',
  noBias: 'No Bias',
  image: 'Image',
  name: 'Name',
  folderName: 'Folder Name',
  folderRemoveConfirm: 'This folder contains lorebooks. Are you sure you want to delete it and all its contents?',
  emotion: 'Emotion Name',
  value: 'Value',
  reroll: 'Regenerate',
  previousGreeting: 'Previous greeting',
  nextGreeting: 'Next greeting',
  rerollCandidate: 'Response',
  newReroll: 'New reroll',
  chatList: 'Chat List',
  pinChat: 'Pin chat',
  unpinChat: 'Unpin chat',
  pinnedChats: 'Pinned chats',
  unnamedPinnedChat: 'Chat',
  openPinnedChat: (chatName: string, characterName: string) => `Open ${chatName} with ${characterName}`,
  homePinnedChatsEmpty: 'Pin a chat to keep it within easy reach here.',
  homeShowAllPinnedChats: (count: number) => `Show all ${count} pinned chats`,
  homeShowFewerPinnedChats: 'Show fewer pinned chats',
  homeRecentCharacters: 'Recent characters',
  homeRecentCharactersEmpty: 'Characters you open or chat with will appear here.',
  homeViewAllCharacters: 'View all',
  homeExplore: 'Explore',
  cardManagement: {
    libraryTitle: 'Card library',
    detailTitle: 'Card details',
    closeDetail: 'Close card details',
    preparing: 'Preparing…',
    characterIntroduction: 'About this person',
    bestTopics: 'Things to talk about',
    playerIdentity: 'Your role',
    initialGoal: 'Initial goal',
    title: 'Card management',
    description: 'Browse and manage published character and story packages.',
    search: 'Search title, tags or release ID',
    librarySearch: 'Search characters or stories',
    kind: 'Card type',
    allKinds: 'All cards',
    character: 'Character',
    story: 'Story',
    status: 'Publication status',
    allStatuses: 'All statuses',
    listed: 'Published',
    unlisted: 'Unlisted',
    packageFile: 'CHARX package',
    sourceId: 'Original release ID (optional revision)',
    upload: 'Upload CHARX',
    uploadHint: 'Up to 64 MiB. New uploads are unlisted; changed content requires a new release ID.',
    invalidUpload: 'Choose a .charx file no larger than 64 MiB.',
    failed: 'Request failed:',
    count: (count: number) => `${count} cards`,
    refresh: 'Refresh',
    loading: 'Loading cards…',
    loadTimeout: 'Loading timed out. Please retry.',
    empty: 'No matching cards.',
    searchResults: (total: number) => `${total} cards found`,
    loadingMore: 'Loading more…',
    loadMoreFailed: 'Could not load more. Please retry.',
    loadMore: 'Load more',
    previous: 'Previous',
    next: 'Next',
    releaseId: 'Release ID',
    packageSize: 'Package size',
    creator: 'Creator',
    registeredAt: 'Registered at',
    revisions: 'Related releases',
    load: 'Load',
    installing: 'Installing…',
    checkingInstall: 'Checking this instance…',
    installCheckFailed: 'Unable to check whether this card is installed.',
    checkAgain: 'Check again',
    installed: 'Loaded in this instance',
    notInstalled: 'Not loaded in this instance',
    loaded: 'Card loaded and ready to use.',
    reused: 'Reusing the installed card.',
    chat: 'Start chatting',
    playStory: 'Start story',
    chooseUser: 'Select a chat user, then click Start chatting again.',
    refreshFailed: 'Card installed, but runtime resources could not be read. Please retry.',
    characterUnavailable: 'The installed card is unavailable.',
    generationRequired: 'Configure the default model, prompt preset and persona in Settings before starting a story.',
    writerRequired: 'Creating a chat requires write access. Use the existing connection controls, then retry.',
    chatFailed: 'Could not create the chat. Please retry.',
    download: 'Download CHARX',
    publish: 'Publish',
    delete: 'Delete card',
    deleteConfirm: (name: string, id: string, chats: number, messages: number) =>
      `Permanently delete “${name}”?\nRelease ID: ${id}\n\nThis removes the shared CHARX and catalog entry, plus ${chats} conversations, ${messages} messages and associated runtime data for all users of this instance.\nThis cannot be undone. Other releases and historical workspaces are retained.`,
    deletionPartial: 'Instance data was deleted; package cleanup is incomplete. Retry the same card. ',
    deletionRecovery: 'Review and clean remaining data',
    deletionRecoveryHint:
      'This release is unavailable or its catalog entry was deleted. If deletion was incomplete, review this release ID and clean up remaining instance data. You will be asked to confirm the current scope.',
    deletionRefreshFailed: 'The instance card data was deleted, but the page could not sync. Refresh the page.',
    withdraw: 'Unlist',
    publicationHint:
      'Publication controls this catalog. Installed cards and existing conversations keep their releases.',
    published: 'Published.',
    withdrawn: 'Unlisted. The package is retained.',
    uploaded: 'Package saved. Check its publication status in the details.',
  },
  generatingMessage: 'Generating message',
  pinChatQueued: 'The pin change is saved on this device and queued for the server.',
  pinChatFailed: 'The chat pin could not be saved. Please try again.',
  chatListExportAll: 'Export all chats',
  chatListDeleteAllAfterExportConfirm:
    'All chats were downloaded. Do you want to delete every chat for this character?',
  chatListDeleteAllSecondConfirm:
    'This will permanently delete every chat for this character. Are you absolutely sure?',
  chatListDeleteAllExportChanged: 'Chats changed since the export. Re-export them and try again.',
  chatExportColdStorageBlocked: (chats: string[]) =>
    `Export stopped because these chats still use cold-storage pointers: ${chats.join(', ')}. Open each affected chat once to recover it, then export again.`,
  chatListDeleteAllAction: 'Delete all chats',
  chatListImport: 'Import chat',
  chatListEdit: 'Edit chat list',
  chatListCreateFolder: 'Create chat folder',
  chatStructurePending: (action: string) => `Saving ${action}…`,
  chatStructureQueued: (action: string) => `${action} is saved on this device and queued. It will retry automatically.`,
  chatStructureFailed: (action: string) => `${action} could not be saved. The previous chat list was restored.`,
  chatCreateProvisional: (name: string) =>
    `${name} is available provisionally. Its creation is queued and will retry automatically.`,
  selectChatToOpen: 'Select a chat from the list to start.',
  openMostRecentChat: 'Open most recent chat',
  removeChat: 'Remove this message?',
  loreBook: 'Lorebook',
  character: 'Character',
  Chat: 'Chat',
  globalLoreInfo: 'Character Lorebook applys to all chats in the character.',
  localLoreInfo: 'Chat Lorebook only applies to the current chat.',
  removeConfirm: 'Do you really want to remove: ',
  removeConfirm2: 'Do you REALLY want to remove: ',
  exportConfirm: 'Do you want to export this?',
  insertOrder: 'Insertion Order',
  activationKeys: 'Activation keys',
  activationKeysInfo: 'Comma seperated',
  prompt: 'Prompt',
  loreBookDepth: 'Lorebook Search Depth',
  loreBookToken: 'Lorebook Max Tokens',
  removeCharacter: 'Remove Character',
  exportCharacter: 'Export Character',
  userSetting: 'User Settings',
  username: 'Your Name',
  userIcon: 'Your Icon',
  successExport: 'Successfuly exported and downloaded to your download directory',
  successImport: 'Successfuly imported',
  importedCharacter: 'Imported Character',
  alwaysActive: 'Always Active',
  agentOnlyLorebook: 'Agent input only',
  agentOnlyLorebookDescription:
    'Excluded from normal prompts. Agents can require this character or chat entry by its display name.',
  additionalPrompt: 'Additional Prompt',
  descriptionPrefix: 'Description Prefix',
  forceReplaceUrl: 'Reverse Proxy',
  emotionWarn: 'Auxiliary model is used.',
  plugin: 'Plugin',
  language: 'Language',
  UiLanguage: 'UI Language',
  createfromScratch: 'Create from Scratch',
  importCharacter: 'Import Character',
  disabled: 'Disabled',
  noPluginSelected: 'Model selected as plugin, but no plugin selected.',
  text: 'Text',
  UISize: 'Chat Text Size',
  newVersion: 'Update found, do you want to install?',
  remindLaterQuestion: 'When should I remind you?',
  remindIgnore: 'Ignore',
  remindLater1Day: 'Remind in 1 day',
  remindLater3Days: 'Remind in 3 days',
  remindLater5Days: 'Remind in 5 days',
  remindLater1Week: 'Remind in 1 week',
  display: 'Display & Audio',
  useCustomBackground: 'Custom Background',
  translateInput: 'Translate Input',
  autoTranslation: 'Auto Translation',
  autoTranslateBotOnly: 'Translate bot messages only',
  bilingualDisplay: 'Bilingual Mode',
  bilingualEmphasis: 'Emphasize',
  bilingualEmphasisOriginal: 'Original',
  bilingualEmphasisTranslation: 'Translation',
  fullscreen: 'Fullscreen',
  playMessage: 'Play Message Audio',
  iconSize: 'Icon Size',
  single: 'Single',
  multiple: 'Multiple',
  useCharLorebook: 'Use Lores in Characters',
  selectChar: 'Select Character',
  theme: 'Theme',
  fastifyTheme: 'Fastify',
  editOrder: 'Edit Order',
  autoMode: 'Auto Mode',
  submodel: 'Auxiliary Model',
  timeOutinSec: 'Timeout (in seconds)',
  emotionPrompt: 'Emotion Prompt',
  singleView: 'Single View',
  SpacedView: 'Multiple Character View',
  emphasizedView: 'Double Character View',
  pluginWarn: 'Installing malicious plugins can cause problems.',
  pluginConfirm: 'Do you really want to import this plugin? Only import plugins from trusted sources.',
  pluginContainsExternalMedia: 'This plugin contains external media.',
  pluginContainsExternalJS: 'This plugin contains external Javascript.',
  waifuWidth: 'Waifu Chat Width',
  savebackup: 'Save Backup to google',
  loadbackup: 'Load Backup from google',
  files: 'Files',
  backupRestore: 'Backup & Restore',
  backupUnsupportedGroups: (count: number, groups: string[]) =>
    `This backup contains ${count} unsupported group character${count === 1 ? '' : 's'}${
      groups.length > 0 ? ` (${groups.join(', ')})` : ''
    }. Nothing was imported.`,
  backupUnsupportedStandaloneChatBlocks:
    'This backup stores chats in standalone CHAT blocks, which this version of RisuAI cannot import. Nothing was imported, and your existing data was not changed.',
  backupConfirm: 'Do you really want to save backup?',
  portableSaveSecretsWarning:
    'This export contains your API keys and other secrets. Handle the file like a password. Do you want to continue?',
  backupLoadConfirm: 'Do you really want to load backup? All datas will be lost!',
  backupLoadConfirm2: 'Do you really, really want to load backup? All datas will be lost!',
  backupImportSuccess: 'Local backup loaded.',
  backupImportSuccessWithAssetCaveats: (missingCount: number, orphanedCount: number) => {
    const caveats: string[] = []
    if (missingCount > 0) {
      caveats.push(`${missingCount} referenced asset${missingCount === 1 ? ' is' : 's are'} missing`)
    }
    if (orphanedCount > 0) {
      caveats.push(
        `${orphanedCount} stored asset${orphanedCount === 1 ? ' is' : 's are'} not referenced by the restored data`,
      )
    }
    return `Local backup loaded, but ${caveats.join(' and ')}.`
  },
  backupImportSkippedBlocks: (blocks: string[]) =>
    `The supported backup content was imported. These standalone blocks were skipped:\n${blocks.map((block) => `- ${block}`).join('\n')}`,
  pasteAuthCode: 'Please copy the auth code from popup and paste it in here:',
  others: 'Others',
  presets: 'Presets',
  modelPresets: 'Model presets',
  promptPresets: 'Prompt presets',
  dragModelPreset: 'Drag to reorder model preset',
  dragPromptPreset: 'Drag to reorder prompt preset',
  activePromptPresets: 'Active',
  archivedPromptPresets: 'Archived',
  showActivePromptPresets: 'Show active prompt presets',
  showArchivedPromptPresets: 'Show archived prompt presets',
  archivePromptPreset: 'Archive prompt preset',
  restorePromptPreset: 'Restore prompt preset',
  noActivePromptPresets: 'No active prompt presets',
  noArchivedPromptPresets: 'No archived prompt presets',
  presetCopyName: (name: string) => `${name} Copy`,
  presetSelectionSaving: 'Saving preset selection...',
  presetSelectionQueued: 'The preset selection is saved on this device and queued. It will retry automatically.',
  presetSelectionFailed: 'The preset selection could not be saved. Review the restored selection and try again.',
  presetRenameSaving: 'Saving preset name...',
  presetRenameQueued: 'The preset name is saved on this device and queued. It will retry automatically.',
  presetRenameFailed: 'The preset name could not be saved. The persisted name has been restored; try again.',
  presetMutationSaving: 'Saving preset change...',
  presetMutationQueued: 'This preset change is saved on this device and queued. It will retry automatically.',
  presetMutationFailed: 'The preset change could not be saved. The persisted presets were restored; try again.',
  overrideModelParameters: 'Override model parameters',
  recommendedModelPreset: 'Recommended model preset',
  missingRecommendedModelPreset: (id: string) => `Missing model preset: ${id}`,
  legacyBotPresets: 'Legacy bot presets',
  legacyBotPresetMigration: 'Legacy bot preset migration',
  legacyPresetImportChoice: (fileName: string, primaryModel: string, auxiliaryModel: string) =>
    `“${fileName}” also contains legacy model routing.\n\nPrimary model: ${primaryModel}\nAuxiliary model: ${auxiliaryModel}\n\nImport that model routing too, or keep only the prompt settings?`,
  legacyPresetImportModelAndPrompt: 'Import model routing and prompt',
  legacyPresetImportPromptOnly: 'Keep prompt settings only',
  legacyPresetModelNotSpecified: 'Not specified',
  noLegacyBotPresets: 'No legacy bot presets',
  extractModelAndPrompt: 'Extract model and prompt',
  extractModelOnly: 'Model',
  extractPromptOnly: 'Prompt',
  imageGeneration: 'Image Generation',
  loraScaleLabel: (index: number) => `LoRA ${index} Scale`,
  provider: 'Provider',
  key: 'Key',
  noData: 'No Data',
  currentImageGeneration: 'Current Image Generation Data',
  promptPreprocess: 'Use Prompt Preprocess',
  SwipeRegenerate: 'Use Swipe for Regeneration',
  instantRemove: 'Remove subsequent when message remove',
  instantRemoveConfirm:
    'Do you want to remove just one message? If you select No, then the message after it will also be remove.',
  textColor: 'Text Color',
  classicRisu: 'Classic Risu',
  highcontrast: 'High-Contrast',
  quickPreset: 'You can quickly change preset by Ctrl + (Index of Preset)',
  requestretrys: 'Request Retrys when Fail',
  utilityBot: 'Utility Bot',
  ShowLog: 'Show Request Logs',
  waifuWidth2: 'Waifu Character Size',
  sayNothing: "Input 'say nothing' when no string inputed",
  regexScript: 'Regex Script',
  type: 'Type',
  editInput: 'Modify Input',
  editOutput: 'Modify Output',
  editProcess: 'Modify Request Data',
  loadLatest: 'Load Latest Backup',
  loadOthers: 'Load Other Backups',
  exampleMessage: 'Example Message',
  creatorNotes: "Creator's Comment",
  systemPrompt: 'System Prompt',
  characterNotes: 'Character Notes',
  personality: 'Personality',
  scenario: 'Scenario',
  alternateGreetings: 'Alternate Greetings',
  unrecommended: 'Not Recommended',
  chatNotes: 'Chat Notes',
  showUnrecommended: 'Show Unrecommended Settings',
  showGlobalLorebookAndRegex: 'Show Legacy Global Lorebook and Regex Menus',
  doNotWarnExternalServers: 'Do not warn about external servers',
  altGreet: 'Alternative First Messages',
  scripts: 'Scripts',
  settings: 'Settings',
  systemSettings: 'System settings',
  settingsItemRemovalConfirm: 'Are you sure you want to remove this item?',
  settingsGroupChatSetup: 'Chat setup',
  settingsGroupCapabilities: 'Tools & Extensions',
  settingsGroupInterface: 'Interface',
  settingsGroupData: 'Data',
  settingsGroupAboutAdvanced: 'Advanced & About',
  settingsNavModelProfiles: 'AI Models & Keys',
  settingsNavPromptPresets: 'Prompts & Templates',
  settingsNavAgentPresets: 'Agent Presets',
  settingsNavInputHooks: 'Input Hooks',
  inputHooks: 'Input Hooks',
  inputHookTypeDraft: 'Draft',
  inputHookTypeBtw: 'BTW',
  inputHookAdd: 'Add Hook',
  inputHookName: 'Hook Name',
  inputHookPrompt: 'Hook Prompt',
  inputHookModel: 'Model',
  inputHookTranslation: 'Translation',
  inputHookInheritOtherAxModel: 'Inherit Other Auxiliary model',
  inputHookDelete: 'Delete Hook',
  inputHookDraftLabel: 'Draft',
  inputHookDraftPlaceholder: 'Hook output will appear here. You can edit it before sending.',
  inputHookSelectDraft: 'Select Draft Hook',
  inputHookDraftDialogTitle: 'Select Draft Hook',
  inputHookNone: 'None',
  inputHookBtw: 'BTW',
  inputHookBtwDialogTitle: 'Run BTW Hook',
  inputHookBtwResult: 'BTW Result',
  inputHookBtwDismiss: 'Dismiss BTW Result',
  inputHookSendDraft: 'Send Draft',
  inputHookConvert: 'Convert',
  settingsNavLegacyBotPresets: 'Legacy Bot Presets',
  settingsNavUserPersona: 'User Persona',
  settingsNavMemory: 'Memory',
  settingsNavDisplayAudio: 'Display & Audio',
  settingsNavLanguage: 'Language',
  settingsNavAccessibility: 'Accessibility',
  settingsNavModules: 'Modules',
  settingsNavPlugins: 'Plugins',
  settingsNavBackups: 'Backup & Restore',
  settingsNavRequestHistory: 'Request History',
  requestHistoryTitle: 'Request History',
  requestHistoryDescription:
    'Review durable LLM request records. Prompts, responses, chat context, and active toggle values may contain private information.',
  requestHistoryLimit: 'Maximum retained records',
  requestHistoryLimitDescription:
    'The newest records are kept and older records are automatically removed. Use 0 to disable recording and clear existing history.',
  requestHistorySaveLimit: 'Save limit',
  requestHistorySaving: 'Saving…',
  requestHistoryRefresh: 'Refresh',
  requestHistoryDisabledHint: 'Request history will be disabled and all existing records will be deleted.',
  requestHistoryLimitSaveFailed: 'The request history limit could not be saved.',
  requestHistoryRecordCount: (count: number) => `${count} request record${count === 1 ? '' : 's'}`,
  requestHistoryDeleteSelected: (count: number) => `Delete selected (${count})`,
  requestHistoryDeleting: 'Deleting…',
  requestHistoryDeleteConfirm: (count: number) =>
    `Permanently delete ${count} selected request record${count === 1 ? '' : 's'}?`,
  requestHistoryLoading: 'Loading request history…',
  requestHistoryLoadingDetail: 'Loading request details…',
  requestHistoryEmpty: 'No request history records are available.',
  requestHistorySelectRecord: 'Select request record',
  requestHistoryDuration: (seconds: string) => `${seconds} s`,
  requestHistoryDurationPending: '—',
  requestHistoryUnknownProfile: 'Unknown profile',
  requestHistoryProfile: 'Profile',
  requestHistoryContext: 'Chat context',
  requestHistoryToggles: 'Toggle states',
  requestHistoryPrompt: 'Finalized prompt',
  requestHistoryResponse: 'Response',
  requestHistoryMetadata: 'Metadata',
  requestHistoryApiMetadata: 'API Metadata',
  requestHistoryError: 'Error',
  settingsNavKeyboardShortcuts: 'Keyboard Shortcuts',
  settingsNavAdvanced: 'Advanced',
  settingsNavSourceCode: 'Source Code',
  settingsNavSupporters: 'RisuAI Supporters',
  settingsNavEasyPanel: 'Easy Panel',
  selective: 'Selective',
  SecondaryKeys: 'Secondary keys',
  useGlobalSettings: 'Use Global Settings',
  recursiveScanning: 'Recursive Scanning',
  creator: 'Creator',
  CharVersion: 'Character Version',
  Speech: 'Speech',
  SuperMemory: 'SupaMemory',
  useExperimental: 'Enable Experimental Features',
  showMemoryLimit: 'Show Memory Limit',
  showFirstMessagePages: 'Show First Message Pages',
  roundIcons: 'Round Icons',
  streaming: 'Streaming',
  halfStreaming: 'Half streaming (show speed only)',
  oobaLegacyBufferedOnlyNotice:
    'Ooba Legacy uses buffered HTTP compatibility mode. Streaming and half streaming are unavailable.',
  halfStreamingTokensPerSecond: (tokensPerSecond: number) => `${tokensPerSecond.toFixed(1)} tokens/s`,
  chatBot: 'Chat Bot',
  otherBots: 'Other Bots',
  user: 'User',
  additionalAssets: 'Additional Assets',
  editDisplay: 'Modify Display',
  community: 'Community',
  sourceCodeTitle: 'Source Code',
  sourceCodeDescription: 'Repositories for the original project and this Fastify fork.',
  sourceCodeOriginalName: 'RisuAI',
  sourceCodeOriginalDescription: 'Original project',
  sourceCodeForkName: 'RisuAI Fastify',
  sourceCodeForkDescription: 'This fork',
  openSourceCodeRepository: (name: string) => `Open the ${name} repository on GitHub`,
  textBackgrounds: 'Custom Text Screen Color',
  textBorder: 'Text Outlines',
  textScreenRound: 'Round Text Screen',
  textScreenBorder: 'Text Screen Borders',
  ttsReadOnlyQuoted: 'Read Only Quoted',
  ttsStop: 'Stop TTS',
  ttsCatalogError: 'Unable to load the TTS voice catalog.',
  characterVoiceAsr: 'Speech recognition (ASR)',
  characterVoiceTts: 'Speech synthesis (TTS)',
  characterVoiceDoubao: 'Doubao',
  characterVoiceFishAsrPending: 'Fish ASR — not connected yet',
  characterVoiceModel: 'Model',
  characterVoiceProviderUnavailable: 'Provider configuration is unavailable. Check server settings.',
  characterVoiceFishSearchPlaceholder: 'Search by voice name',
  characterVoiceFishEmpty: 'No voices found. Change your search or refresh the list.',
  characterVoiceAdvanced: 'Advanced options (manual voice ID)',
  characterVoiceBalanceError: 'Fish balance is insufficient. Please top up.',
  characterVoiceAuthError: 'Fish credentials or access permissions are invalid.',
  characterVoiceConfigError: 'Check the speech service configuration.',
  characterVoiceIdError: 'Select a valid voice and try again.',
  characterVoiceModelError: 'The speech model is not supported.',
  characterVoiceTimeoutError: 'Speech request timed out. Please try again.',
  characterVoiceNetworkError: 'Network connection failed. Please try again.',
  characterVoiceInterruptedError: 'Playback interrupted. Please try again.',
  characterVoiceGenericError: 'Speech service unavailable. Please try again.',
  characterVoiceFishSearch: 'Search my Fish voices',
  characterVoiceRefresh: 'Refresh',
  characterVoicePrevious: 'Previous page',
  characterVoiceNext: 'Next page',
  characterVoiceManualId: 'Voice ID (empty uses server default)',
  characterVoiceFishHint:
    'Save a reusable voice in Fish, then refresh here. A missing list entry will not change your selected ID.',
  characterVoiceInvalidId: 'Enter a 32-character hexadecimal voice ID, or leave it empty.',
  characterVoiceListFailed: 'Could not load Fish voices. Your selected ID is retained.',
  characterVoiceStop: 'Stop preview',
  characterVoiceLegacy: 'Legacy read-aloud settings',
  characterVoiceLegacyHint: 'These settings apply only when no unified character voice is saved.',
  characterVoiceUnconfigured: 'No unified voice configured',
  characterVoiceTitle: 'Cross-app character voice',
  characterVoiceDescription:
    'Previewing never saves changes. StoryTune and other apps use the new voice only after you confirm and save.',
  characterVoiceSearch: 'Search official Volcengine TTS 2.0 voices',
  characterVoiceSearchPlaceholder: 'Voice name, ID, scene, language or tag',
  characterVoiceSelection: 'Default voice',
  characterVoiceSystemDefault: 'Use system default',
  characterVoiceUnknown: 'not in the current official catalog',
  characterVoiceSpeed: 'Speed',
  characterVoicePreviewText: 'Preview text',
  characterVoicePreview: 'Preview without saving',
  characterVoicePreviewing: 'Generating preview…',
  characterVoicePreviewFailed: 'Unable to preview this voice',
  characterVoiceSave: 'Confirm and save',
  characterVoiceSaving: 'Saving…',
  characterVoiceSaved: 'Saved',
  characterVoiceUnsaved: 'Unsaved changes',
  characterVoiceSavePending: 'The voice setting has not been saved yet. Please try again.',
  ttsAdvancedEndpoint: 'Advanced (OpenAI-compatible endpoint)',
  ttsCustomVoiceSeed: 'Custom Voice Seed',
  ttsNormalize: 'Normalize',
  ttsSpeed: 'Speed',
  ttsUseAutoPath: 'Use Auto Path',
  ttsUseLongAudio: 'Use Long Audio',
  ttsUseReferenceAudioScript: 'Use Reference Audio Script',
  ttsVolume: 'Volume',
  askRemoval: 'Ask Removal',
  replaceGlobalNote: 'Global Note Replacement',
  charLoreBook: 'Character Lorebook',
  globalLoreBook: 'Global Lorebook',
  globalRegexScript: 'Global Regex',
  accessibility: 'Accessibility',
  reducedMotion: 'Reduce Motion',
  hypaV3ProgressOpenChatOnly: 'Show Full Memory Progress for Open Chat Only',
  sendWithEnter: 'Send with Enter Key',
  fixedChatTextarea: 'Fixed at the bottom of the chat window',
  floatingChatInput: 'Floating Chat Input',
  openFloatingChatInput: 'Open floating chat input',
  goToBottom: 'Go to bottom',
  hideInput: 'Hide input',
  clickToEdit: 'Click Text to Edit',
  disableAutoPopupMessageEditor: 'Disable Auto Popup Editor When Editing Messages',
  useMonacoEditorOnDesktop: 'Use Monaco Editor on Desktop',
  useMonacoEditorOnMobile: 'Use Monaco Editor on Mobile',
  monacoEditor: 'Monaco editor',
  plainTextEditor: 'Plain text editor',
  enableBlockPartialEdit: 'Enable Block Partial Edit (Hover to edit individual blocks)',
  longPressToPopupEditor: 'Long Press to Open Popup Editor',
  enableDragPartialEdit: 'Enable Drag Partial Edit (Select text to edit)',
  setNodePassword: 'Set your password to security',
  inputNodePassword:
    "Input your password. if you can't remember, remove save/__password.txt in your server files and restart the server.",
  simple: 'Simple',
  advanced: 'Advanced',
  creatingSuggestions: 'Creating Suggestions...',
  orderByOrder: 'Talk by Order',
  removeFromGroup: 'Do you really want to remove {{char}} from group?',
  active: 'Active',
  loreRandomActivation: 'Use Probability Condition',
  activationProbability: 'Probability',
  tags: 'Tags',
  backgroundHTML: 'Background Embedding',
  copied: 'Copied',
  useChatCopy: 'Use Chat Message Copy',
  useChatSticker: 'Use Chat Sticker',
  useAdditionalAssetsPreview: 'Use Additional Assets Preview',
  autoTranslateInput: 'Auto Translate Input',
  enterMessageForTranslateToEnglish: 'Enter Message for Translate to English',
  recent: 'Recent',
  downloads: 'Downloads',
  trending: 'Trending',
  imageCompression: 'Image Compression',
  chatAssumed: '',
  proxyAPIKey: 'Key/Password',
  proxyRequestModel: 'Request Model',
  officialWiki: 'Official Wiki',
  officialWikiDesc: 'Official Wiki for Risuai. Feel free to see.',
  officialDiscord: 'Official Discord',
  officialDiscordDesc: 'Official Discord to talk about Risuai',
  persona: 'Persona',
  icon: 'Icon',
  account: 'Account',
  remove: 'Remove',
  duplicate: 'Duplicate',
  moveUp: 'Move up',
  moveDown: 'Move down',
  able: 'Able',
  assetWidth: 'Asset Images Max Width',
  animationSpeed: 'Animation Speed',
  screenshot: 'Screenshot',
  screenshotSaved: 'Screenshot Saved',
  createBotwithAI: 'Create Bot from AI',
  changeFolderName: 'Input the new folder name (blank to cancel)',
  cancel: 'Cancel',
  renameFolder: 'Rename Folder',
  changeFolderColor: 'Change Folder Color',
  changeFolderImage: 'Change Folder Image',
  askBeforeOpening: (enabled: boolean) => `Ask before opening: ${enabled ? 'On' : 'Off'}`,
  confirmFolderOpening: (name: string) => `Open the folder “${name}”?`,
  organizeCharacters: 'Organize characters',
  characterActionsFor: (name: string) => `Organize ${name}`,
  folderActionsFor: (name: string) => `Folder actions for ${name}`,
  moveToFolder: 'Move to folder',
  moveOutOfFolder: 'Move out of folder',
  createFolderWith: 'Create folder with…',
  chooseDestinationFolder: 'Choose a destination folder',
  chooseFolderPartner: 'Choose a character for the new folder',
  newCharacterFolderName: 'New Folder',
  chooseFolderColor: 'Choose a folder color',
  resetFolderImage: 'Reset to default image',
  selectFolderImageFile: 'Select image file',
  folderColorRed: 'Red',
  folderColorGreen: 'Green',
  folderColorBlue: 'Blue',
  folderColorYellow: 'Yellow',
  folderColorIndigo: 'Indigo',
  folderColorPurple: 'Purple',
  folderColorPink: 'Pink',
  folderColorDefault: 'Default',
  fullWordMatching: 'Full Word Matching',
  botSettingAtStart: 'Bot Menu when Launch',
  triggerStart: 'On chat Send',
  triggerInput: "On user's output",
  triggerOutput: "On character's output",
  triggerManual: 'Manual trigger only',
  triggerCondVar: 'If Variable',
  triggerCondExists: 'If Text Exists on Chat',
  triggerScript: 'Trigger Script',
  triggerMatchRegex: 'Match with Regex',
  triggerMatchLoose: 'Match Loosely',
  triggerMatchStrict: 'Match Strictly',
  searchDepth: 'Search Depth',
  equal: 'Equal to',
  notEqual: 'Not Equal to',
  greater: 'Greater than',
  less: 'Less than',
  greaterEqual: 'Greater or Equal to',
  lessEqual: 'Less or Equal to',
  triggerEffSysPrompt: 'Add System Prompt',
  triggerEffSetVar: 'Modify Variable',
  triggerEffImperson: 'Send Chat',
  triggerEffCommand: 'Run Command',
  triggerEffRunTrigger: 'Run Trigger',
  triggerEffStop: 'Stop Sending Prompt',
  triggerEffCall: 'Call Trigger',
  varableName: 'Variable Name',
  role: 'Role',
  location: 'Location',
  promptstart: 'Start of Prompt',
  promptend: 'End of Prompt',
  historyend: 'End of History',
  always: 'Always',
  noEffect: 'No Effect',
  effect: 'Effect',
  invaildTriggerEffect: "This effect doesn't works for this trigger type.",
  operator: 'Operator',
  TriggerSetToVar: 'Set to Variable',
  TriggerAddToVar: 'Add to Variable',
  TriggerSubToVar: 'Subtract from Variable',
  TriggerMulToVar: 'Multiply to Variable',
  TriggerDivToVar: 'Divide from Variable',
  isNull: 'is not set',
  ifChatIndex: 'If chat index',
  ifRandom: 'If random',
  ifValue: 'If Value',
  sendExternalServerWarning:
    'Continuing will send a request to an external server, and your IP address may be transmitted. Do you want to continue?',
  hideAllImages: 'Hide All Images',
  popularityLevel: '{} Popularity',
  colorScheme: 'Color Scheme',
  colorSchemePresetNames: {
    default: 'Default',
    dark: 'Dark',
    light: 'Light',
    cherry: 'Cherry',
    galaxy: 'Galaxy',
    nature: 'Nature',
    ocean: 'Ocean',
    aurora: 'Aurora',
    twilight: 'Twilight',
    realblack: 'Real Black',
    'monokai-light': 'Monokai Light',
    'monokai-black': 'Monokai Black',
    'sky-light': 'Sky Light',
    'sage-light': 'Sage Light',
    'lavender-light': 'Lavender Light',
    'slate-light': 'Slate Light',
    lite: 'Lite',
    custom: 'Custom',
  },
  rangeStart: 'Range Start',
  rangeEnd: 'Range End',
  untilChatEnd: 'Until Chat End',
  usePromptTemplate: 'Use Prompt Template',
  specialType: 'Special Type',
  noSpecialType: 'No Special Type',
  forceProxyAsOpenAI: 'Force Proxy Format as OpenAI',
  promptTemplate: 'Prompt Template',
  customInnerFormat: 'Custom Inner Format',
  innerFormat: 'Inner Format',
  HypaMemory: 'HypaMemory',
  ToggleHypaMemory: 'Toggle HypaMemory',
  resetPromptTemplateConfirm: 'Do you really want to reset prompt template?',
  emotionMethod: 'Emotion Method',
  continueResponse: 'Continue Response',
  showMenuChatList: 'Show Menu Chat List',
  translatorType: 'Translator Type',
  htmlTranslation: 'HTML Translate',
  deeplKey: 'deepL API Key',
  deeplFreeKey: 'deepL Free API Key',
  deeplXUrl: 'deepLX URL',
  deeplXToken: 'deepLX Token',
  exportPersona: 'Export Persona',
  importPersona: 'Import Persona',
  export: 'Export',
  import: 'Import',
  supporterThanks: 'Supporter Thanks',
  supporterThanksDesc: 'Thank you for your support!',
  supporterOriginalProjectNotice:
    'The supporters listed here are patrons of the original RisuAI project and are not affiliated with this fork. Support given through the links below goes to the original RisuAI project.',
  donatorPatreonDesc:
    "For default, it will not be shown in the list for privacy. If you want to show your nickname, go to Risuai's patreon page and press the link button.",
  useNamePrefix: 'Use Name Prefix',
  textAdventureNAI: 'Run as Text Adventure',
  appendNameNAI: 'Append Name on NAI',
  customStopWords: 'Custom Stop Words',
  defaultPrompt: 'Default Prompt',
  additionalText: 'Additional Description',
  additionalTextUnsupported:
    'Unsupported on this server: imported additional-description data is preserved but is not added to prompts.',
  seed: 'Seed',
  charjs: 'CharacterJS',
  depthPrompt: 'Depth Prompt',
  largePortrait: 'Portrait',
  lorePlus: 'LoreBook+',
  reverseProxyOobaMode: 'Ooba Mode',
  exactTokens: 'Exact Tokens',
  fixedTokens: 'Approximate Tokens',
  inlayViewScreen: 'Inlay Screen',
  imgGenPrompt: 'Image Generation Prompt',
  imgGenNegatives: 'Image Generation Negative Prompt',
  imgGenInstructions: 'Image Generation Instructions',
  usePlainFetchWarn: 'Please disable this option when using NovelAI, as it can cause CORS errors when using NovelAI.',
  translationPrompt: 'Translation Prompt',
  translationResponseSize: 'Translation Response Size',
  webdeeplwarn: 'This option can cause CORS errors.',
  saveServerBackup: 'Save Server Backup',
  loadServerBackup: 'Load Server Backup',
  automaticSafetySnapshot: 'Automatic safety snapshot',
  saveBackupLocal: 'Save local backup',
  saveBackupLocalZipStyle: 'Save local backup in ZIP style (not recommended).',
  loadBackupLocal: 'Load Backup Locally',
  backupQueuedChangesDiscarded:
    'The backup was loaded, but queued changes from the replaced database were discarded. Please review the restored values.',
  topP: 'Top P',
  genTimes: 'Generation Choices',
  cot: 'Chain of Thoughts',
  forcePlainFetch: 'Force Plain Fetch',
  autoFillRequestURL: 'Autofill Request URL',
  newOAIHandle: 'New OpenAI Handling',
  inlayImage: 'Inlay Image Feature',
  nativeAutomark: 'Experimental Native Automark',
  assistantPrefill: 'Assistant Prefill',
  postEndInnerFormat: 'Post End',
  sendChatAsSystem: 'Send Chat as System',
  sendName: 'Send Name on Non-group Chat',
  utilOverride: 'Utility Override',
  template: 'Template',
  chatAsOriginalOnSystem: 'Send as original role',
  exportAsDataset: 'Export Save as Dataset',
  editTranslationDisplay: 'Edit Translation Display',
  selectModel: 'Select Model',
  autoRemoveThoughtTag: 'Remove Thought Tag',
  customChainOfThought: 'Custom Chain of Thoughts',
  maxThoughtTagDepth: 'Max Thought Tag Depth',
  openRouterFallback: 'Use Fallback',
  openRouterMiddleOut: 'Use Middle Out',
  openRouterProviderOrder: 'Provider Preference Order',
  openRouterProviderOnly: 'Allowed Providers',
  openRouterProviderIgnore: 'Ignored Providers',
  openRouterSearchModel: 'Search models...',
  openRouterSortByName: 'Name',
  openRouterSortByPrice: 'Price',
  openRouterSortByProvider: 'Provider',
  openRouterSortAsc: 'Asc',
  openRouterSortDesc: 'Desc',
  geminiApiKey: 'Gemini API Key',
  removePunctuationHypa: 'Memory Punctuation Removal',
  memoryLimitThickness: 'Memory Limit Thickness',
  inputCardPassword: 'Input Card Password',
  ccv2Desc: 'Character Card V2 is a format widely used in chatbot programs.',
  ccv3Desc: 'Character Card V3 is a next generation format that is used in chatbot programs.',
  rccDesc: 'Risu Refined Character Card is a format with additional features like password, integrity check and etc.',
  password: 'Password',
  license: 'License',
  licenseDesc: "You can choose license for the downloaders to limit the usages of your card's prompt.",
  passwordDesc: 'You can set a password to protect your card from unauthorized access.',
  largePersonaPortrait: 'Persona Portrait',
  module: 'Module',
  modules: 'Modules',
  personaModuleLink: 'Module Link',
  personaModuleLinkInfo: 'Linked modules are active while this Persona is selected for a chat.',
  personaModuleLinkActive: 'Active from Persona',
  promptPresetModuleIntegrationActive: 'Active from Prompt Preset',
  agentPresetModuleIntegrationActive: 'Active from Agent Preset',
  moduleIntegrationActive: 'Active from Module Integration',
  noLinkableModules: 'No linkable modules are installed.',
  noModules: 'No modules has been installed.',
  noModuleDescription: 'No description provided',
  createModule: 'Create Module',
  basicInfo: 'Basic Info',
  moduleContent: 'Module Content',
  confirmRemoveModuleFeature: 'Do you really want to remove this feature? This action cannot be undone.',
  editModule: 'Edit Module',
  moduleSave: {
    saving: 'Saving...',
    queued: 'The module change is saved on this device and queued. It will retry automatically.',
    commandConflict: 'The server has newer module data. Your draft was kept. Review it and try again.',
    commandUnavailable: 'Module saving is unavailable. Your draft was kept.',
    editTargetMissing: 'The module you were editing no longer exists. Your draft was kept.',
    draftStorageFailed:
      'This module draft could not be stored for reload recovery. Keep this editor open or export the draft before leaving.',
    recoveredDraft: 'Recovered Module Draft',
    copyDraft: 'Copy Draft',
    exportDraft: 'Export Draft',
    discardDraft: 'Discard Draft',
    leaveEditorConfirm: 'This module has unsaved changes. Leave the editor? Your draft will be kept for recovery.',
    discardChangesConfirm: 'Discard unsaved module changes? This cannot be undone.',
    draftCopied: 'The recovered module draft was copied.',
    commandError: (error: string) => `Module save failed: ${error}`,
  },
  promptTemplateMutation: {
    saving: 'Saving prompt template...',
    queued: 'The prompt template change is saved on this device and queued. It will retry automatically.',
    commandConflict:
      'The server has newer prompt-template data. The previous template was restored; review it and try again.',
    commandUnavailable: 'Prompt-template saving is unavailable. The previous template was restored.',
    commandError: (error: string) => `Prompt-template save failed: ${error}`,
    replayDiscarded: 'The queued prompt-template change could not be applied. The server version was restored.',
  },
  moduleImport: {
    commandConflict: 'The server changed while the module was being imported. The module was not imported. Try again.',
    commandUnavailable: 'Module import is unavailable. The module was not imported.',
    commandError: (error: string) => `Module import failed: ${error}`,
    mcpPrompt: 'Enter the URL of the MCP module to import:',
    mcpInvalidUrl: 'Enter a valid MCP URL.',
    mcpNotFound: 'The MCP module could not be found or did not return valid metadata.',
    mcpDescription: (url: string) => `MCP from ${url}`,
    mcpSuccess: (name: string) => `MCP module imported successfully!\nName: ${name}`,
    queued: 'The MCP module is saved locally and will finish importing automatically.',
  },
  importModule: 'Import Module',
  download: 'Download',
  edit: 'Edit',
  enableGlobal: 'Enable Globally',
  chatModulesInfo:
    'You can enable or disable modules for this chat. You can also enable for this character by right clicking or long pressing the enable button.',
  sideMenuRerollButton: 'Side Menu Reroll Button',
  persistentStorage: 'Persistent Storage',
  persistentStorageSuccess: 'Storage is successfully persisted',
  persistentStorageFail: 'Storage is not persisted. Did you deny the request?, or is the browser not supported?',
  persistentStorageRecommended: 'Persistent Storage Recommended',
  persistentStorageDesc:
    'Your browser supports persistent storage. This is recommended for better performance and user experience.',
  enable: 'Enable',
  disable: 'Disable',
  postFile: 'Post File',
  requestInfoInsideChat: 'Show Request Info Inside Chat',
  inputTokens: 'Input Tokens',
  outputTokens: 'Output Tokens',
  tokenWarning: 'Token caculation can be inaccurate. It is recommended to use it as a reference.',
  log: 'Log',
  popularityLevelDesc: 'Popularity increases with downloads, etc. To estimate, 3.7 popularity is about 1 downloads.',
  additionalParams: 'Additional Parameters',
  applyAdditionalParamsToAll: 'Apply Additional Parameters to All Models',
  heightMode: 'Height Mode',
  useAdvancedEditor: 'Use Advanced Editor',
  noWaitForTranslate: 'No Wait for Translate',
  antiClaudeOverload: 'Anti-Claude Overload',
  activeTabChange: 'Current tab is inactivated since other tab is active. To activate this tab, click OK',
  addCharacter: 'Add Character',
  random: 'Random',
  metaData: 'Meta Data',
  removeIncompleteResponse: 'Remove Incomplete Sentences',
  tokenizer: 'Tokenizer',
  tokenizerOptions: {
    automatic: 'Automatic (model default)',
    tiktokenCl100kBase: 'Tiktoken (cl100k_base)',
    tiktokenO200kBase: 'Tiktoken (o200k_base)',
    mistral: 'Mistral',
    llama: 'Llama',
    novelAI: 'NovelAI',
    claude: 'Claude',
    novelList: 'NovelList',
    llama3: 'Llama 3',
    gemma: 'Gemma',
    cohere: 'Cohere',
    deepSeek: 'DeepSeek',
    deepSeekV4: 'DeepSeek V4',
    glm4: 'GLM-4',
    glm5: 'GLM-5',
  },
  chatFormating: 'Chat Formating',
  useInstructPrompt: 'Use Instruction Prompt',
  openRouterInstructPromptUnsupported:
    'Unsupported in Fastify server mode: OpenRouter always sends chat messages, so this toggle has no effect.',
  textAreaSize: 'Input Area Size',
  textAreaTextSize: 'Input Area Text Size',
  sideBarSize: 'Sidebar Size',
  embedding: 'Embedding',
  voyageContext4: 'Voyage Context 4',
  run: 'Run',
  noMessage: 'Type something to start the chat.',
  combineTranslation: 'Combine Translation',
  dynamicAssets: 'Dynamic Assets',
  dynamicAssetsEditDisplay: 'Use Dynamic Assets in Display',
  longTermMemory: 'Long Term Memory',
  grid: 'Grid',
  list: 'List',
  trash: 'Trash',
  trashDesc:
    'Deleted characters are moved to trash. You can restore or delete them permanently. Deleted characters are automatically purged after 3 days.',
  shareExport: 'Export',
  risupresetDesc: 'Risupreset format is a format specifically designed for Risuai presets.',
  risuMDesc: 'RisuM format is a format that is specifically designed for Risuai modules.',
  jsonDesc: 'JSON format is a format that is easy to read and write for both humans and machines.',
  nickname: 'Nickname',
  useRegexLorebook: 'Use Regex',
  customPromptTemplateToggle: 'Custom Toggles',
  defaultVariables: 'Default Variables',
  lowLevelAccess: 'Low Level Access',
  resultStoredVar: 'Variable to Store Result',
  triggerEffRunLLM: 'Run Main Model',
  triggerEffectSendAI: 'Resend AI',
  triggerEffectUnsupportedOnServer:
    'Unsupported on this server: this imported effect is preserved but has no effect during generation.',
  regexEmotionEffectUnsupportedOnServer:
    'Unsupported on this server: @@emo is preserved but is skipped during generation.',
  triggerConfigurationUnsupportedDiagnostic: (effectTypes: string[], cbsCallbacks: string[]) =>
    `This configuration contains server-unsupported definitions. They are preserved but blocked during generation.${effectTypes.length > 0 ? ` Effects: ${effectTypes.join(', ')}.` : ''}${cbsCallbacks.length > 0 ? ` CBS callbacks: ${cbsCallbacks.join(', ')}.` : ''}`,
  triggerImportUnsupportedDiagnostic: (effectTypes: string[], cbsCallbacks: string[]) =>
    `The imported definitions were kept unchanged, but unsupported behavior will be blocked during generation.${effectTypes.length > 0 ? ` Effects: ${effectTypes.join(', ')}.` : ''}${cbsCallbacks.length > 0 ? ` CBS callbacks: ${cbsCallbacks.join(', ')}.` : ''}`,
  triggerEffectRuntimeUnsupported: (effectType: string) =>
    `Trigger effect “${effectType}” is unsupported on this server and was skipped.`,
  cbsCallbackRuntimeUnsupported: (callbackName: string) =>
    `CBS callback “${callbackName}” is unsupported on this server and returned an empty value.`,
  cbsClientContextUnavailable: (callbackName: string) =>
    `CBS callback “${callbackName}” could not resolve because browser context was unavailable.`,
  triggerEffCheckSim: 'Check Similarity',
  triggerEffShowAlert: 'Show Alert',
  normal: 'Normal',
  error: 'Error',
  input: 'Input',
  select: 'Select',
  options: 'Options',
  dismissNotice: 'Dismiss',
  legacyMemoryMigrationNotice: (algorithms: string) =>
    `Legacy memory migration required\n\nThese selected memory modes no longer run in this server-backed version: ${algorithms}.\n\nSwitch to the maintained Hypa V3 memory in settings. This notice appears once for this database.`,
  alertContent: 'Alert Content',
  lowLevelAccessConfirm:
    'This content uses Low Level Access. which means this content can access the AI model and your storage directly. Do you really want to import this content?',
  triggerLowLevelOnly:
    'This trigger only works with Low Level Access. To enable this trigger, enable Low Level Access in advanced settings in the character or module.',
  truthy: 'Truthy',
  extractRegex: 'Extract Text with Regex',
  runImgGen: 'Run Image Generation',
  cutChat: 'Cut Chat',
  modifyChat: 'Modify Chat',
  regex: 'Regex',
  flags: 'Flags',
  resultFormat: 'Result Format',
  negPrompt: 'Negative Prompt',
  start: 'Start',
  end: 'End',
  index: 'Index',
  search: 'Search',
  goCharacterOnImport: 'Go to Character on Import',
  format: 'Format',
  v2Warning: 'Warning: V2 card is no longer supported. It may not contain all the data.',
  applyModule: 'Apply Module',
  successApplyModule: 'Module is successfully applied',
  moduleApply: {
    queued: 'The module changes are saved locally and will retry automatically.',
    commandConflict: 'The module could not be applied because the character changed on the server.',
    commandUnavailable: 'The module could not be saved. Please try again.',
    commandError: (error: string) => `The module could not be saved: ${error}`,
  },
  font: 'Font',
  customFont: 'Custom Font',
  chatScreenWidth: 'Chat Screen Width',
  lineHeight: 'Line Height',
  paragraphBreakBySentences: 'Paragraph Break Every N Sentences',
  paragraphBreakSentenceCount: 'Sentences Per Paragraph',
  notCharxWarn:
    'This character uses multiple assets. It is recommended to export this character as a CharX format for better compatibility.',
  storyCardCharxRequired:
    'Story Card must be exported as a Character Card V3 CharX file to preserve its cast, scenes, and discovery cover.',
  noPlugins: 'No Plugins Installed',
  legacyTranslation: 'Legacy Translation',
  clipboardSuccess: 'Copied to Clipboard',
  translateContent: 'Translate Content',
  doNotTranslate: 'Do Not Translate',
  includePersonaName: 'Include Persona Name',
  hidePersonaName: 'Hide Persona Name',
  triggerSwitchWarn: 'If you change the trigger type, current triggers will be lost. Do you want to continue?',
  codeMode: 'Code',
  blockMode: 'Block',
  helpBlock: 'Help',
  hideChatIcon: 'Hide Icon UI',
  loadInternalBackup: 'Load Internal Backup',
  createCopy: 'Create a Copy',
  bindPersona: 'Bind Persona',
  chatOptions: 'Chat Options',
  doYouWantToBindCurrentPersona: 'Do you want to bind the current persona to this chat?',
  doYouWantToUnbindCurrentPersona: 'Do you want to unbind the persona from this chat?',
  personaBindedSuccess: 'Persona is successfully binded',
  personaUnbindedSuccess: 'Persona is successfully unbinded',
  personaBindingFailed: 'Could not update the persona binding. Please try again.',
  personaBindingQueued: 'The persona binding is saved on this device and queued. It will retry automatically.',
  parameters: 'Parameters',
  sizeAndSpeed: 'Size and Speed',
  useLegacyGUI: 'Use Legacy GUI',
  claudeCachingExperimental: 'Claude Caching',
  openClose: 'Open/Close',
  hideApiKeys: 'Hide API Keys',
  unformatQuotes: 'Disable Quote Formatting',
  enableDevTools: 'Enable Dev Tools',
  selectFile: 'Select File',
  namespace: 'Namespace',
  moduleIntergration: 'Module Integration',
  previewInfo: 'This preview shows prompt before model-specific processing.',
  miscTools: 'Misc Tools',
  customCSS: 'Custom CSS',
  enableScrollToActiveChar: 'Enable Scroll to Active Character',
  menu: 'Menu',
  jsonSchema: 'JSON Schema',
  enableJsonSchema: 'Enable Schema',
  strictJsonSchema: 'Strict Schema',
  extractJson: 'Extract JSON',
  reloadSession: 'Newer version of save data is found. Reloading the session...',
  observerShell: {
    title: 'Read-only preview',
    status:
      'You can browse while this tab waits for write access. Your latest navigation will be applied when it is safe to save.',
    statusRetrying: 'Trying to restore write access without leaving this preview…',
    statusUnavailable: 'Write access is temporarily unavailable. This preview remains available.',
    statusAuthLost: 'Your authenticated preview was cleared because this session is no longer authorized.',
    statusPromoted: 'Write access is restored. Finishing the transition to the full app…',
    retryWriter: 'Retry write access',
    retryingWriter: 'Retrying write access…',
    readOnlyBadge: 'Read only',
    navigationLabel: 'Read-only navigation',
    charactersLabel: 'Characters',
    noCharacters: 'No characters are available.',
    openCharacter: (name: string) => `Open ${name}`,
    summaryLabel: 'Summary preview',
    detailsLabel: 'Read-only details',
    chatCount: (count: number) => `${count} ${count === 1 ? 'chat' : 'chats'}`,
    summaryHelp:
      'This compact summary is available before write access. You can optionally load more details without saving changes.',
    loadDetails: 'Load read-only details',
    loadDetailsFor: (name: string) => `Load read-only details for ${name}`,
    loadingDetails: 'Loading details…',
    retryDetails: 'Retry details',
    detailsError: 'Details could not be loaded. The summary remains available.',
    chatsLabel: 'Chats',
    pinnedChatsHelp: 'Pinned chats available in the summary:',
    openChat: (name: string) => `Open chat ${name}`,
    noChats: 'No chat details are loaded.',
    chooseCharacter: 'Choose a character',
    chooseCharacterHelp: 'Character summaries remain available while this tab waits for write access.',
  },
  serverWriteUnavailable:
    'This change was not saved because write access is unavailable. Wait for startup or retry connecting.',
  pendingMutationRecoveryReload:
    'A queued change could not be applied safely. Reloading to restore the latest server state...',
  pendingChatMessageQueued:
    'Your message is saved on this device and queued. It will be sent when the server is available; generation has not started.',
  pendingMutationReplayRetained:
    'Some pending changes still could not reach the server. They remain saved on this device and must be retried before the app can load.',
  insecureOriginWarning:
    'RisuAI is being served over plain HTTP. Network traffic is not encrypted, and on-device autosave encryption is weaker. Use HTTPS or access it through localhost when possible.',
  pendingMutationDiscarded:
    'One or more pending changes could not be applied safely and were discarded. Please review your recent edits.',
  pendingMutationDiscardedDetail: (scope: string, error: string) =>
    `Affected scope: ${scope}\nServer response: ${error}`,
  serverDatabaseDamaged:
    'The server database appears damaged and cannot be initialized safely. Restore risu.db or a database backup, then restart the server.',
  personaImportQueued:
    'The persona is saved on this device and queued. It will finish importing when the server is available.',
  personaImportFailed: 'Could not import the persona. Please try again.',
  personaMutationQueued: 'The persona change is saved on this device and queued. It will retry automatically.',
  personaMutationFailed: 'The persona change could not be saved. The previous state was restored.',
  personaIconSaveQueued:
    'The persona image is saved on this device and queued. It will finish saving when the server is available.',
  personaIconSaveFailed: 'Could not save the persona image. Please try again.',
  translatorPresetPersistence: {
    saving: 'Saving translator preset changes...',
    queued: 'The translator preset change is saved on this device and queued. It will retry automatically.',
    failed: 'Could not save the translator preset change. Please try again.',
  },
  translatorPipeline: {
    steps: 'Translation steps',
    addStep: 'Add step',
    duplicateStep: 'Duplicate step',
    removeStep: 'Remove step',
    moveUp: 'Move step up',
    moveDown: 'Move step down',
    stepName: 'Step name',
    enabled: 'Enabled',
    model: 'Model',
    inheritTranslateModel: 'Inherit translate model',
    outputKey: 'Output key (optional)',
    outputKeyPlaceholder: 'draft',
    invalidOutputKey:
      'Use a unique key up to 64 characters, beginning with a letter or underscore and containing only letters, numbers, or underscores.',
    malformedHistorySlot:
      'History slots must use {{slot::history::N}} or {{slot::historytrans::N}}, where N is from 1 to 50.',
    slotHelp:
      '{{slot::content}} always inserts the original source; {{slot::prev}} inserts the previous step; {{slot::out::key}} inserts a prior named output.',
    copySuffix: 'Copy',
  },
  presetImportQueued:
    'The preset is saved on this device and queued. It will finish importing when the server is available.',
  presetImportFailed: 'Could not save the imported preset. Please try again.',
  fixMarkdownNewline: 'Fix Markdown Newline',
  customQuotes: 'Custom Quotes',
  leadingSingleQuote: 'Leading Single Quote',
  leadingDoubleQuote: 'Leading Double Quote',
  trailingSingleQuote: 'Trailing Single Quote',
  trailingDoubleQuote: 'Trailing Double Quote',
  translatorNote: "Translator's Note",
  formatGroupInSingle: 'Format Group in Single',
  defineCustomGUI: 'Define Custom GUI',
  chatHTML: 'Chat HTML',
  logShare: 'Show Share Log Button',
  preview: 'Preview',
  recommended: 'Recommended',
  newChat: 'New Chat',
  predictedOutput: 'Predicted Output',
  systemContentReplacement: 'System Content Replacement',
  systemRoleReplacement: 'System Role Replacement',
  seperateParameters: 'Separate Parameters',
  seperateParametersEnabled: 'Enable Separate Parameters',
  summarizationPrompt: 'Summarization Prompt',
  reSummarizationPrompt: 'Re-Summarizaion Prompt',
  translatorPrompt: 'Translation Prompt',
  translateBeforeHTMLFormatting: 'Translate Before HTML Formatting',
  translatorSendTextAsIs: 'Send Text As-Is',
  translatorExcludeThoughts: 'Exclude Chain-of-Thought',
  translatorHistoryMaxTokens: 'Translation History Max Tokens',
  retranslate: 'Retranslate',
  retranslateConfirm: 'Retranslate this message? This will replace the current translation.',
  editTranslation: 'Edit Translation',
  editTranslationSave: 'Save Translation',
  exportTranslationCache: 'Export Translation Cache',
  exportTranslationCacheSuccess: 'Translation cache exported successfully.',
  importTranslationCache: 'Import Translation Cache',
  importTranslationCacheConfirm:
    'This will merge entries into your translation cache. Existing keys will be overwritten. Continue?',
  importTranslationCacheSuccess: 'Imported {0} translation cache entries.',
  importTranslationCacheFailed: 'Imported {0} entries, but {1} entries failed to save. Storage may be full.',
  exportTranslationCacheEmpty: 'Translation cache is empty.',
  clearTranslationCache: 'Clear Translation Cache',
  clearTranslationCacheSuccess: 'Translation cache cleared successfully.',
  clearTranslationCacheConfirm: 'This will delete all translation cache entries. This cannot be undone. Continue?',
  loading: 'Loading',
  save: 'Save',
  pluginImport: 'Plugin Import',
  messageInput: 'Message input',
  moreActions: 'More actions',
  cancelGeneration: 'Cancel generation',
  stickers: 'Stickers',
  readMessageAloud: 'Read message aloud',
  close: 'Close',
  resizeCharacterImage: 'Resize character image',
  openCharacter: (name: string) => `Open ${name}`,
  restoreCharacter: (name: string) => `Restore ${name}`,
  deleteCharacterPermanently: (name: string) => `Delete ${name} permanently`,
  noSearchResults: 'No matching characters found.',
  characterHasEmotionImages: 'This character includes emotion images',
  characterHasAdditionalAssets: 'This character includes additional assets',
  characterHasLorebook: 'This character includes a lorebook',
  autoTranslateCachedOnly: 'Auto-translate Cached Messages Only',
  notification: 'Notification',
  autoTranslateNotificationDeferCapSeconds: 'Delay completion notification for translation (seconds, 0 = no limit)',
  permissionDenied: 'Permission Denied by Your Browser or OS',
  pushNotifications: {
    hydrating: 'Checking pending notification cleanup…',
    startupCleanup: 'Retrying pending notification cleanup…',
    enabling: 'Setting up browser notifications…',
    disabling: 'Cleaning up browser notifications…',
    compensating: 'Turning the notification setting back off…',
    retryingCompensation: 'Retrying the notification setting update…',
    retryingStorage: 'Retrying device notification storage…',
    retryingCleanup: 'Retrying notification cleanup…',
    compensationAccepted: 'The notification setting was restored to off.',
    compensationQueued: 'Turning notifications off is queued and will retry automatically.',
    compensationFailed: 'The notification setting could not be turned off. Retry this update.',
    retryCompensation: 'Retry turning off',
    retryStorage: 'Retry device storage',
    retryCleanup: 'Retry cleanup',
    pendingCleanup: (count: number) =>
      `${count} server notification ${count === 1 ? 'endpoint is' : 'endpoints are'} pending cleanup.`,
    localInspectionPending: 'Browser notification cleanup still needs verification.',
    cleanupRetryStorageFailed: 'Pending notification cleanup could not be saved on this device.',
    operationFailed: 'The notification operation failed unexpectedly. Please retry.',
    retryOperation: 'Retry notification operation',
    setupFailures: {
      notificationUnavailable: 'This browser does not provide the Notification API.',
      permissionDefault: 'Notification permission was not granted.',
      serviceWorkerUnavailable: 'The notification service worker could not be started.',
      pushUnavailable: 'This browser does not provide push notifications.',
      vapidUnavailable: 'The server is not configured with a push notification key.',
      subscriptionFailed: 'The browser could not create a push notification subscription.',
      serverRegistrationFailed: 'The server could not register this browser for push notifications.',
      unexpected: 'Push notification setup failed unexpectedly.',
    },
    cleanupFailures: {
      serviceWorker: 'The browser notification service is unavailable, so local cleanup could not be verified.',
      subscriptionInspection: 'The browser subscription could not be inspected.',
      localUnsubscribe: 'The browser subscription could not be removed.',
      serverDeletion: 'The server subscription could not be removed.',
      unexpected: 'Notification cleanup did not finish.',
    },
  },
  customFlags: 'Custom Flags',
  enableCustomFlags: 'Enable Custom Flags',
  googleCloudTokenization: 'Google Cloud Tokenization',
  presetChain: 'Preset Chain',
  legacyMediaFindings: 'Legacy Media Findings',
  staticsDisclaimer: 'The statistics are based on the data from after July 2024. The data may not be accurate.',
  reset: 'Reset',
  assetMaxDifference: 'Asset Max Difference',
  menuSideBar: 'Menu Side Bar',
  home: 'Home',
  showSavingIcon: 'Show Saving Icon',
  banCharacterset: 'Auto Regenerate On Characterset',
  showPromptComparison: 'Show Prompt Comparison',
  inlayErrorResponse: 'Inlay Error Response',
  triggerOn: 'Trigger On',
  noConfig: 'No Config',
  varName: 'Variable Name',
  var: 'Variable',
  condition: 'Condition',
  trigger: 'Trigger',
  outputVar: 'Output Variable',
  source: 'Source',
  cmd: 'Command',
  sysStart: 'System Start',
  sysHistoryEnd: 'System History End',
  sysPromptEnd: 'System Prompt End',
  target: 'Target',
  addElse: 'Add Else',
  min: 'Min',
  max: 'Max',
  delimiter: 'Delimiter',
  deleteCount: 'Delete Count',
  triggerEffRunAxLLM: 'Run Auxiliary Model',
  hypaV3Settings: {
    descriptionLabel: 'HypaMemory V3 is a long-term memory system that uses both summarization and vector search.',
    supaMemoryPromptPlaceHolder: 'Leave it blank to use default',
    maxMemoryTokensRatioLabel: 'Max Memory Tokens Ratio (Estimated)',
    maxMemoryTokensRatioError: 'Unable to calculate Max Memory Tokens Ratio',
    memoryTokensRatioLabel: 'Memory Tokens Ratio',
    extraSummarizationRatioLabel: 'Extra Summarization Ratio',
    maxChatsPerSummaryLabel: 'Max Messages Per Summary',
    queryChatCountLabel: 'Query Chat Count',
    summaryChunkSeparatorLabel: 'Chunk Separator Regex',
    recentMemoryRatioLabel: 'Recent Memory Ratio',
    similarMemoryRatioLabel: 'Similar Memory Ratio',
    randomMemoryRatioLabel: 'Random Memory Ratio',
    enableSimilarityCorrectionLabel: 'Enable Similarity Correction',
    preserveOrphanedMemoryLabel: 'Preserve Orphaned Memory',
    applyRegexScriptWhenRerollingLabel: 'Apply Regex Script When Rerolling',
    doNotSummarizeUserMessageLabel: 'Do Not Summarize User Message',
  },
  hypaV3Progress: {
    activeJobs: (count: number) => `${count} active memory ${count === 1 ? 'job' : 'jobs'}`,
    activeInOtherChats: (count: number) => `${count} active memory ${count === 1 ? 'job' : 'jobs'} in other chats`,
    otherChatsCompact: (count: number) => `+${count} other ${count === 1 ? 'chat job' : 'chat jobs'}`,
    openDetailsAction: (count: number) => `Show details for ${count} active memory ${count === 1 ? 'job' : 'jobs'}`,
    closeDetailsAction: 'Hide memory job details',
    unnamedChat: 'Unnamed chat',
    unknownChat: (id: string) => `Chat ${id}`,
    kind: {
      chunk: 'Preparing memory',
      embed: 'Searching memory',
      summarize: 'Summarizing memory',
    },
    pending: 'Waiting',
    running: 'Running',
    attempt: (attemptCount: number, maxAttempts: number) => `Attempt ${attemptCount}/${maxAttempts}`,
  },
  hypaV3Modal: {
    titleLabel: 'HypaV3',
    searchAction: 'Search summaries',
    previousSearchResultAction: 'Previous search result',
    nextSearchResultAction: 'Next search result',
    importantFilterAction: 'Show important summaries',
    bulkEditAction: 'Bulk edit summaries',
    categoryManagerAction: 'Manage categories',
    settingsAction: 'Open HypaV3 settings',
    moreActionsAction: 'More actions',
    selectedFilterAction: 'Show selected summaries',
    resetDataAction: 'Reset HypaV3 data',
    closeAction: 'Close HypaV3',
    selectSummaryAction: 'Select summary #{0}',
    deselectSummaryAction: 'Deselect summary #{0}',
    summaryCategoryLabel: 'Category for summary #{0}',
    toggleSummaryTranslationAction: 'Show or hide summary translation',
    toggleSummaryImportantAction: 'Toggle the summary important mark',
    rerollSummaryAction: 'Reroll summary',
    deleteSummaryAction: 'Delete summary',
    deleteFollowingSummariesAction: 'Delete following summaries',
    toggleRerolledSummaryTranslationAction: 'Show or hide rerolled summary translation',
    cancelRerollAction: 'Cancel summary reroll',
    applyRerollAction: 'Apply rerolled summary',
    toggleConnectedMessageTranslationAction: 'Show or hide connected message translation',
    bulkCategoryLabel: 'Category for selected summaries',
    toggleBulkImportantAction: 'Toggle the important mark for selected summaries',
    bulkSelectionInputLabel: 'Summary numbers to select',
    toggleResummaryTranslationAction: 'Show or hide re-summarization translation',
    retryResummaryAction: 'Retry re-summarization',
    applyResummaryAction: 'Apply re-summarization result',
    cancelResummaryAction: 'Cancel re-summarization',
    addCategoryAction: 'Add category',
    saveCategoryAction: 'Save category',
    cancelCategoryEditAction: 'Cancel category editing',
    editCategoryAction: 'Edit category',
    deleteCategoryAction: 'Delete category',
    tagNameLabel: 'Tag name',
    saveTagAction: 'Save tag',
    cancelTagEditAction: 'Cancel tag editing',
    editTagAction: 'Edit tag',
    deleteTagAction: 'Delete tag',
    refreshMemoryJobsAction: 'Refresh memory jobs',
    resetConfirmMessage: 'This action cannot be undone. Do you want to reset HypaV3 data?',
    resetConfirmSecondMessage: 'This action is irreversible. Do you really, really want to reset HypaV3 data?',
    convertLabel: 'No summaries yet, but you may convert HypaV2 data to V3.',
    convertButton: 'Convert to V3',
    convertSuccessMessage: 'Successfully converted HypaV2 data to V3',
    convertErrorMessage: 'Failed to convert HypaV2 data to V3: {0}',
    noSummariesLabel: 'No summaries yet',
    searchPlaceholder: 'Enter #N, ID, or query',
    summaryNumberLabel: 'Summary #{0}',
    inactiveSummaryModelLabel: 'Not used for current memory (model: {0})',
    discardFailedSummaryChangesConfirm: 'Saving failed. Discard unsaved summary text and close?',
    deleteThisConfirmMessage: 'Delete this summary?',
    deleteAfterConfirmMessage: 'Delete all summaries after this one?',
    deleteAfterConfirmSecondMessage: 'This action cannot be undone. Are you really sure?',
    translationLabel: 'Translation',
    rerolledSummaryLabel: 'Rerolled Summary',
    rerolledTranslationLabel: 'Rerolled Summary Translation',
    connectedMessageCountLabel: 'Connected Messages ({0})',
    connectedFirstMessageLabel: 'First Message',
    connectedMessageRoleLabel: "{0}'s Message",
    connectedMessageNotFoundLabel: 'Message not found',
    connectedMessageLoadingError: 'Error loading connected message: {0}',
    connectedMessageTranslationLabel: 'Translation',
    nextSummarizationFirstMessageLabel: 'First Message',
    nextSummarizationNoMessageIdLabel: 'No Message ID',
    nextSummarizationLabel: 'HypaV3 will summarize [{0}]',
    nextSummarizationNoMessagesFoundLabel: 'WARN: No messages found',
    nextSummarizationLoadingError: 'Error loading next summarization target: {0}',
    summarizationConditionLabel: 'Tip: HypaV3 begins summarization when input tokens exceed the maximum context size.',
    emptySelectedFirstMessageLabel: 'WARN: Selected first message is empty',
    unclassified: 'Unclassified',
    reSummarize: 'Re-summarize',
    reSummarizing: 'Re-summarizing...',
    reSummarizeResult: 'Re-summarization result',
    retry: 'Retry',
    translate: 'Translate',
    tag: 'Tag',
    tagManager: 'Tag Manager',
    tagManagerTitle: 'Tag Manager - Summary #{0}',
    newTagName: 'New tag name',
    noTagsYet: 'No tags yet.',
    addNewTagHint: 'Add a new tag above.',
    categoryManager: 'Categories',
    allCategories: 'All',
    categoryName: 'Category name',
    noCategoriesYet: 'No categories yet.',
    addNewCategoryHint: 'Click the + button above to add one.',
  },
  bulkEnabling: 'Lorebook Bulk Enabling',
  showTranslationLoading: 'Show Translation Loading',
  showDeprecatedTriggerV1: 'Show Deprecated Trigger V1',
  showDeprecatedTriggerV2: 'Show Deprecated Trigger V2',
  triggerV1Warning: 'Trigger V1 is deprecated. It might be removed in the future.',
  copy: 'Copy',
  paste: 'Paste',
  depth: 'Depth',
  returnCSSError: 'Return CSS Error',
  alwaysActiveInChat: 'Always Active (Current Chat)',
  childLoreDesc:
    "This is a copy of Character lore that remains 'Always Active' until removed or manually deactivated in the original.",
  thinkingTokens: 'Thinking Tokens',
  thinkingType: 'Thinking Mode',
  adaptiveThinkingEffort: 'Adaptive Thinking Effort',
  antiServerOverload: 'Anti-Server Overload',
  localActivationInGlobalLorebook: 'Local Activation in Global Lorebook',
  globalLorebookDelete: {
    deleting: 'Deleting this lorebook…',
    queued: 'Deletion is queued on this device and will retry automatically.',
    failed: 'This lorebook could not be deleted. Try again.',
  },
  scopedLorebookMutation: {
    pending: 'Saving lorebook changes…',
    queued: 'This lorebook change is queued on this device and will retry automatically.',
    failed: (detail: string) => `This lorebook change could not be saved.${detail ? ` ${detail}` : ''}`,
    localActivationCleanupQueued:
      'Removing this entry from the active chat is queued on this device and will retry automatically.',
    localActivationCleanupFailed: (detail: string) =>
      `This entry's chat-local activation could not be removed and was restored.${detail ? ` ${detail}` : ''}`,
  },
  cachePoint: 'Cache Point',
  all: 'All',
  showFolderNameInIcon: 'Show Folder Name in Icon',
  claudeCachingRetrival: 'Claude Caching Retrival',
  claudeCachingRetrivalDesc:
    'Extends the cache time for Claude Caching, by requesting every 4 minutes. This can reduce the cache miss rate, but it can increase the cost if its not used properly.',
  automaticCachePoint: 'Automatic Cache Point',
  experimentalChatCompression: 'Experimental Chat Data Handling',
  loadingChat: 'Loading chat…',
  loadingChatData: 'Loading Chat Data',
  chatDataLoadFailed: 'Chat data could not be loaded.',
  promptTemplateLoadFailed: 'Prompt template could not be loaded.',
  promptTemplateSelectedFallbackNotice:
    'This preset uses the selected fallback prompt template. It is shown read-only to prevent replacing it accidentally.',
  loadingLorebookData: 'Loading Lorebook Data',
  lorebookDataLoadFailed: 'Lorebook data could not be loaded.',
  retry: 'Retry',
  chatPostGenerationProgressUnknownScript: 'Script',
  chatPostGenerationProgressCharacterScript: (name: string) => `Character script: ${name}`,
  chatPostGenerationProgressModuleScript: (name: string) => `Module script: ${name}`,
  chatPostGenerationProgressWithComment: (owner: string, comment: string) => `${owner} · ${comment}`,
  chatPostGenerationProgressEditOutput: 'editOutput',
  chatPostGenerationProgressOnOutput: 'onOutput',
  chatPostGenerationProgressTranslating: 'Translating generated message…',
  chatPostGenerationProgressLabel: (owner: string, phase: string, calls: number, waiting: number) =>
    `${owner} ${phase} · LLM calls: ${calls} · Waiting: ${waiting}`,
  outputImageModal: 'Output Image Modal',
  playMessageOnTranslateEnd: 'Play Audio on Translate Completion',
  auxModelUnderModelSettings: 'Show Separate Aux Models in Model Settings',
  seperateModelsForAxModels: 'Separate Models for Auxiliary Models',
  axModelsDef: 'Ax Models Definition',
  doNotChangeSeperateModels: 'Do Not Change Separate Models on Preset Change',
  tools: 'Tools',
  action: 'Action',
  hotkey: 'Hotkey',
  hotkeyDesc: {
    reroll: 'Reroll',
    unreroll: 'Undo Reroll',
    translate: 'Translate',
    remove: 'Remove',
    edit: 'Edit',
    copy: 'Copy',
    send: 'Send',
    settings: 'Settings',
    home: 'Home',
    presets: 'Quick Presets Select',
    persona: 'Quick Persona Select',
    modelSelect: 'Quick Model Select',
    toggleCSS: 'Toggle CSS',
    prevChar: 'Previous Character',
    nextChar: 'Next Character',
    quickMenu: 'Quick Menu',
    quickSettings: 'Quick Settings',
    toggleVoice: 'Toggle Voice',
    toggleLog: 'Toggle Log',
    previewRequest: 'Preview Assembled Prompt',
    import: 'Import',
    export: 'Export',
    webcam: 'Toggle Webcam',
    focusInput: 'Focus Input',
    scrollToActiveChar: 'Scroll to Active Character',
    popupEditor: 'Popup Editor',
    loadout: 'Loadout',
  },
  screenTooSmall: 'Screen is too small to show the interface.',
  advancedModelSettings: 'Advanced Model Settings',
  fallbackModel: 'Fallback Model',
  fallbackWhenBlankResponse: 'Fallback When Blank Response',
  doNotChangeFallbackModels: 'Do Not Change Fallback Models on Preset Change',
  customModels: 'Custom Models',
  igpPrompt: 'IGP Prompt',
  useTokenizerCaching: 'Tokenizer Caching',
  hypaMemoryV3Modal: 'Hypa V3 Modal',
  showMenuHypaMemoryModal: 'Show Menu Hypa Modal',
  promptInfoInsideChat: 'Add Prompt Info to Chat',
  promptTextInfoInsideChat: 'Add Prompt Text to Chat',
  promptInfoEmptyMessage: 'No prompt information is available for this message.',
  promptInfoEmptyToggle: 'No custom toggles are currently active.',
  promptInfoEmptyText: 'No prompt text has been saved.',
  escapeOutput: 'Escape Output',
  openAIFlexProcessing: 'OpenAI Flex Processing',
  claudeBatching: 'Claude Batching',
  claude1HourCaching: 'Claude 1 Hour Caching',
  folderNameInput: 'Please input the new folder name',
  folderRemoveLengthError: 'To remove a folder, it must not contain any entries.',
  personaNote: 'Persona Note',
  mcpAccessPrompt: '{{tool}} is trying to "{{action}}". Do you want to allow this?',
  rememberToolUsage: 'Remember tool usage',
  simplifiedToolUse: 'Simplified tool usage',
  toolCalled: "Tool '{{tool}}' Called",
  static: 'Static',
  dynamic: 'Dynamic',
  assetStyle: 'Asset Style',
  insertAssetPrompt: 'Insert Asset Prompt',
  requestLocation: 'Request Location',
  newImageHandlingBeta: 'New Image Handling (Beta)',
  settingsExported: 'Settings for bug report exported and copied to clipboard.',
  settingsExportedWithoutClipboard:
    'Settings for bug report exported, but clipboard access was unavailable. Use the downloaded file instead.',
  bookmarks: 'Bookmarks',
  noBookmarks: 'No Bookmarks',
  bookmarkAskNameOrDefault: 'Enter bookmark name. If not entered, it will be automatically assigned.',
  bookmarkAskNameOrCancel: 'Enter bookmark name. If not entered, rename will be canceled.',
  collapseAll: 'Collapse All',
  expandAll: 'Expand All',
  noRequestLogs: 'No request logs available.',
  hideErrorDetails: 'Hide Error Details',
  showErrorDetails: 'Show Error Details',
  dynamicOutput: 'Dynamic Output',
  autoAdjustSchema: 'Auto Adjust Schema',
  dynamicMessages: 'Dynamic Messages',
  dynamicMemory: 'Dynamic Memory',
  dynamicResponseTiming: 'Dynamic Response Timing',
  showTypingEffect: 'Show Typing Effect',
  dynamicRequest: 'Dynamic Request',
  dynamicOutputPrompt: 'Dynamic Output Prompt',
  settingsCloseButtonSize: 'Settings Close Button Size',
  chatDisplayTailCount: 'Initial Chat Messages to Display',
  chatLoadInitialPages: 'Initial Chat Load Count',
  chatLoadAdditionalPages: 'Additional Chat Load Count',
  translating: 'Translating...',
  stackTraceTranslationFailed: 'Stack trace translation failed. Showing original obfuscated stack trace below.',
  apply: 'Apply',
  add: 'Add',
  nightlyWarning:
    '## Nightly Builds\n\nYou are connecting to a nightly build of the software. This version is meant for testing and most likely contains bugs. **In this version, even if you encounter issues, we do not support resolving them directly.**',
  pluginRiskDetectedAlert:
    'Risuai detected code that may be harmful which can STOLE YOUR API KEY OR PERSONAL DATA. We recommend you to NOT INSTALL THIS PLUGIN.',
  doNotInstall: 'Do Not Install',
  continueAnyway: 'Continue Anyway',
  pluginNoRiskButAlert:
    'Risuai did not detect harmful code, but please be aware that installing plugins from untrusted sources can pose security risks.',

  pluginRisksInuserFriendly: {
    eval: 'Evaluating Code from Strings',
    globalAccess: 'Accessing Global Variables',
    thisOutsideClass: "Using 'this' Outside of Class Context",
    errorInVerification: 'Errors During Verification Process',
    storageAccess: 'Accessing Storage',
  },
  pluginRisksInuserFriendlyDesc: {
    eval: 'The plugin attempts to execute code from strings, strings can be obsucated to hide malicious code.',
    globalAccess:
      'The plugin tries to access global variables, which can lead to unintended side effects or security vulnerabilities.',
    thisOutsideClass:
      "The plugin uses 'this' keyword outside of class context, which can lead to unintended behavior or security issues.",
    errorInVerification:
      'There are errors during the verification process of the plugin, indicating potential issues with its functionality.',
    storageAccess:
      'The plugin attempts to access storage, which could lead to data leaks or unauthorized data manipulation.',
  },
  generatedByAIDisclaimer: 'This content was generated by AI and may contain inaccuracies or biases.',
  aiGenerationWarning: 'Contents will be AI generated and may contain inaccuracies or biases.',
  duplicatePluginFoundUpdateIt: 'Duplicate plugin found. do you want to update the existing plugin?',
  pluginUpdateFoundInstallIt: 'Plugin update found. Do you want to install the update?',
  pluginCheckForUpdates: 'Check for plugin updates',
  pluginCheckingForUpdates: 'Checking for plugin updates…',
  pluginUpdateAvailableVersion: 'Plugin update available: {}',
  pluginInstallUpdateVersion: 'Install plugin update {}',
  pluginUpToDate: 'Plugin is up to date.',
  pluginUpdatePermissionDenied: 'Plugin update permission was denied.',
  pluginUpdateCheckFailed: 'Could not check for plugin updates. Try again.',
  pluginInstallingUpdate: 'Installing plugin update…',
  pluginUpdateInstallFailed: 'Could not install plugin update. Try again.',
  pluginUpdateInstalled: 'Plugin update installed.',
  pluginMutation: {
    saving: 'Saving plugin changes…',
    queued: 'Plugin changes are saved on this device and will retry automatically.',
    failed: 'Plugin changes could not be saved. Try again.',
  },
  goToChat: 'Go to Chat',
  bookmark: 'Bookmark',
  translate: 'Translate',
  branch: 'Branch',
  branchConfirm: 'Do you want to create a branch from this message?',
  branchedText: 'This chat has been branched from {}.',
  loadMore: 'Load More',
  disableMessage: 'Disable Message',
  disableAbove: 'Cut Messages for AI',
  autoScrollToNewMessage: 'Auto scroll to new message',
  alwaysScrollToNewMessage: 'Always scroll to new message (ignore scroll position)',
  newMessage: 'New Message',
  newMessageButtonStyle: 'New Message Button Style',
  newMessageButtonBottomCenter: 'Bottom Center (Default)',
  newMessageButtonBottomRight: 'Bottom Right',
  newMessageButtonBottomLeft: 'Bottom Left',
  newMessageButtonFloatingCircle: 'Floating Circle (Bottom Right)',
  newMessageButtonRightCenter: 'Right Center',
  newMessageButtonTopBar: 'Top Bar',
  trimStartNewChat: "Trim 'Start New Chat' Messages",
  pluginCompatibilityMode: 'Plugin Compatibility Mode',
  strictScriptCheck: 'Strict Script Check',
  complexRegexCompatibilityMode: 'Complex Regex Compatibility',
  complexRegexStrictMode: 'Strict',
  complexRegexWorkerMode: 'Compatibility',
  complexRegexInputTimeoutMs: 'Input Regex Timeout (ms)',
  complexRegexOutputTimeoutMs: 'Output Regex Timeout (ms)',
  complexRegexDisplayTimeoutMs: 'Display Regex Timeout (ms)',
  pluginDevelopMode: 'Plugin Develop Mode',
  pluginNetworkConsent:
    "Plugin {} is requesting access through RisuAI's public-internet network helper. This request may send chat or account data to third parties. The helper blocks private, local, metadata, and RisuAI service targets. Do you want to allow this?",
  pluginUpdateSourceConsent:
    "RisuAI will check and download updates for plugin {{plugin}} only from its declared HTTPS source:\n{{url}}\nThis permission is limited to update checks and downloads; it does not grant the plugin's runtime network helpers. Do you want to continue?",
  v3RuntimeConsent:
    'Plugin {} is requesting permission to run trusted browser UI code. It can contact public, private, local, metadata, or RisuAI network targets and send chat or account data outside RisuAI, including through browser APIs that bypass network helper consent. Only run plugins you fully trust. Do you want to continue?',
  fetchLogConsent:
    'Plugin {} is requesting to fetch logs, which may expose sensitive information. Do you want to allow this?',
  getFullDatabaseConsent:
    'Plugin {} is requesting to access the full database, which may expose sensitive information. Do you want to allow this?',
  mainDomAccessConsent:
    'Plugin {} is requesting to access the main Document, which may expose sensitive information. Do you want to allow this?',
  replacerPermissionConsent:
    'Plugin {} is requesting permission to replace content in the chat, which may be used to manipulate the conversation. Do you want to allow this?',
  providerPermissionConsent:
    'Plugin {} is requesting permission to access the provider, which may allow it to make unauthorized API calls. Do you want to allow this?',
  sendChatConsent:
    'Plugin {} is requesting permission to send chat messages on your behalf, which will trigger AI responses. Do you want to allow this?',
  createFolderOnBranch: 'Create Folder on Branch',
  hamburgerButtonBottom: 'Move Menu Button to Bottom of Sidebar',
  partialBackupFirstConfirm:
    '⚠️ WARNING: Partial Local Backup ⚠️\n\nThis backup quickly saves only essential information.\n\nIncluded:\n- Database (chat history, characters, modules, plugins, prompts, settings, etc.)\n- Character profile icons (main images)\n- User icons and custom backgrounds\n- Persona icons\n- Folder images\n- Bot preset images\n\n⚠️ NOT Included:\n- Emotion images\n- Additional character assets\n- VITS voice files\n- All other additional media files\n\nThis backup file does NOT include all character assets!\nIf you need a complete backup, use the regular local backup.\n\nDo you want to continue?',
  partialBackupSecondConfirm:
    "⚠️⚠️ FINAL WARNING ⚠️⚠️\n\nThis backup saves only character profile images and does NOT save most other assets!\n\nWhat will be saved:\n- Database (chat history, characters, modules, plugins, prompts, settings, etc.)\n- Character profile icons (main images)\n- User icons and custom backgrounds\n- Persona icons\n- Folder images\n- Bot preset images\n\nWhat will NOT be saved:\n- Emotion images\n- Additional character assets\n- VITS voice files\n- All other media files\n\n⚠️ Proceed ONLY if you don't need character assets in your backup file! ⚠️\n\nAre you really sure you want to proceed with partial backup?",
  savePartialLocalBackup: 'Save Partial Backup Locally (Excluding Character Assets)',
  pluginProviderNotFound: 'Unknown Plugin detected. Please change the model or enable the corresponding plugin.',
  bootstrap: {
    dataCorruptionDetected: (name: string, type: string) =>
      `Data Corruption Detected\n\nModule "${name}" has corrupted lorebook format.\n\nCorrupted data type: ${type}`,
    reportErrorQuestion:
      'Would you like to report this error to the developer?\nThis helps prevent the same error from happening again.\n(Only includes Corrupted module info, no personal data)',
    diagnosticInformation: (reportData: string) =>
      `# Diagnostic Information\n\nPlease copy the information below and report it to the developer via GitHub Issues, Discord, or community.\nDouble-check for any sensitive information before reporting.\n\n\`\`\`json\n${reportData}\n\`\`\``,
    resetLorebookQuestion:
      'Do you want to reset ONLY the lorebook data in this specific corrupted module?\n(Other modules and character data will NOT be affected)\n\nChoosing "No" will keep the data but errors may continue.',
  },
  partialEdit: {
    editButtonTooltip: 'Edit',
    deleteButtonTooltip: 'Delete',
    deleteModalTitle: 'Delete Confirmation',
    deleteConfirmMessage: 'Are you sure you want to delete this part?',
    deleteYes: 'Yes',
    deleteNo: 'No',
    editModalTitle: 'Partial Edit',
    matchFound: (method: string) => `Match: ${method}`,
    matchFailedTitle: 'Cannot Partial Edit',
    matchFailedMessage:
      'Unable to locate precisely in the original message. This may happen with very long sentences or duplicate content. Please use the full message edit feature instead.',
    selectMatch: 'Select which block to edit',
    selectDeleteMatch: 'Select which block to delete',
    matchesFound: 'matches found',
    lineNumber: (line: number) => `Line ~${line}`,
    saveShortcut: 'Save (Ctrl+Enter)',
    cancelShortcut: 'Cancel (Esc)',
    save: 'Save',
    cancel: 'Cancel',
  },
  enableRemoteSaving: 'Enable Remote Saving',
  blockquoteStyling: 'Blockquote Styling',
  dynamicModelRegistry: 'Dynamic Model Registry',
  confirmInstallPluginViaPlugin:
    'You are trying to install a plugin via another plugin. This can be dangerous if the source plugin is not trustworthy. Do you want to continue?',
  risuaiProTools: 'Risuai Pro Tools',
  enableRisuaiProTools: 'Enable Risuai Pro Tools',
  easyPanel: 'Easy Panel',
  mainModel: 'Main Model',
  epEnabled: 'Easy Panel Override',
  epRequirementsNotMet:
    'Easy Panel requirements not met. Click the button below to automatically adjust your settings for optimal Easy Panel settings.',
  seperateParametersByModel: 'Separate Parameters by Model',
  disableSeperateParameterChangeOnPresetChange: 'Disable Separate Parameter Change on Preset Change',
  keepSessionAlive: 'Keep Session Alive',
  keepSessionAliveSound: 'Via Sound',
  // NanoGPT Dashboard
  nanoGPTLoadingAccountInfo: 'Loading account info…',
  nanoGPTCreditBalance: 'Credit Balance:',
  nanoGPTSubscription: 'Subscription',
  nanoGPTGraceUntil: (date: string) => `until ${date}`,
  nanoGPTNoActiveSubscription: 'No active subscription.',
  nanoGPTCancelsAtPeriodEnd: (date: string) => `Cancels at period end (${date})`,
  nanoGPTWeeklyTokens: (pct: string) => `Weekly Included Input Tokens — ${pct} used`,
  nanoGPTDailyTokens: (pct: string) => `Daily Included Input Tokens — ${pct} used`,
  nanoGPTDailyImages: (pct: string) => `Daily Included Images — ${pct} used`,
  nanoGPTResets: (date: string) => `Resets ${date}`,
  nanoGPTUsed: (n: string) => `${n} used`,
  nanoGPTRemaining: (n: string) => `${n} remaining`,
  nanoGPTRenews: (date: string) => `Renews: ${date}`,
  nanoGPTCouldNotLoadAccountInfo: 'Could not load account info.',
  nanoGPTWeeklyTokensLabel: 'Weekly Included Input Tokens',
  nanoGPTDailyTokensLabel: 'Daily Included Input Tokens',
  nanoGPTDailyImagesLabel: 'Daily Included Images',
  nanoGPTUsedLabel: 'used',
  nanoGPTRemainingLabel: 'remaining',
  nanoGPTResetsLabel: 'Resets',
  nanoGPTRenewsLabel: 'Renews:',
  // NanoGPT Provider Picker
  nanoGPTProvider: 'Provider',
  nanoGPTProviderPayAsYouGoOnly: '(only available in pay-as-you-go mode)',
  nanoGPTProviderAuto: 'Auto',
  nanoGPTProviderFree: 'Free',
  nanoGPTProviderInput: 'Input',
  nanoGPTProviderOutput: 'Output',
  nanoGPTProviderQuantization: 'Quantization',
  nanoGPTProviderUndisclosed: 'Undisclosed',
  nanoGPTProviderCache: 'Cache',
  nanoGPTProviderCacheSupported: 'Supported',
  nanoGPTProviderCacheNotSupported: 'Not supported',
  nanoGPTProviderCacheRead: 'Cache read',
  // NanoGPT Settings
  nanoGPTUseSubscriptionEndpoint: 'Use subscription endpoint & models',
  // Model Grid
  modelGridCouldNotLoad: 'Could not load model list. Check your API key.',
  modelGridNoModelsMatch: (q: string) => `No models match "${q}"`,
  modelGridContext: (n: string) => `Context: ${n}`,
  loadouts: 'Loadouts',
  loadout: 'Loadout',
  loadoutPresetHydrationFailed: 'Could not load the preset for this loadout. Please try again.',
  loadoutApplyFailed: 'Could not apply this loadout. Please try again.',
  loadoutApplyQueued: 'Changes are saved locally and queued. You do not need to apply this loadout again.',
  loadoutSaveFailed: 'Could not save this loadout. Please try again.',
  loadoutSaveQueued: 'This loadout is saved locally and queued. You do not need to save it again.',
  loadoutFavoriteFailed: (name: string) => `Could not update favorites for “${name}”. Please try again.`,
  loadoutFavoriteQueued: (name: string) => `The favorite change for “${name}” is saved locally and queued.`,
  loadoutDeleteFailed: (name: string) => `Could not remove “${name}”. Please try again.`,
  loadoutDeleteQueued: (name: string) => `Removal of “${name}” is saved locally and queued.`,
  loadoutModal: {
    title: 'Select Loadout',
    load: 'Load',
    modules: 'Modules',
    globalVariables: 'Global Variables',
    preset: 'Preset',
    persona: 'Persona',
    presetName: (name: string) => `Preset: ${name}`,
    addFavorite: 'Add to favorites',
    removeFavorite: 'Remove from favorites',
    remove: 'Remove loadout',
    removeConfirm: (name: string) => `Remove the loadout “${name}”?`,
    recentlyUsed: 'Recently Used',
    recentlyUsedWithCharacter: 'Recently Used with This Character',
    favorites: 'Favorites',
    all: 'All Loadouts',
    empty: 'No loadouts saved yet.',
    namePlaceholder: 'Loadout name…',
  },

  nanoGPTSelectFromList: 'Select from List',
  nanoGPTManualInput: 'Manual Input',
  nanoGPTManualModelSelect: 'Manual Model Select',
  coldStorage: 'Cold Storage',
  cleanColdStorage: 'Clean Unused Cold Storage',
  customSidebarConfig: 'Custom Sidebar Configuration',
  cleanColdStorageConfirm:
    "This will permanently delete all unused cold storage data. This may contain data that isn't currently used but may be useful in the future. Do you want to continue?",
  modelRoles: {
    title: 'Model Roles',
    globalProviderNotice: 'Provider credentials and provider-specific settings below are global.',
    close: 'Close',
    resolvedProfile: 'Resolved Profile',
    provider: 'Provider',
    requestModel: 'Request Model',
    fallbackCount: (count: number) => `${count} fallback${count === 1 ? '' : 's'}`,
    providerUnavailable: (reason: string) => `Unavailable: ${reason}`,
    modelSelection: 'Model Selection',
    inherit: 'Inherit',
    override: 'Override',
    inheritedModel: 'Inherited model',
    sourceBase: 'Base field',
    sourceOverride: 'Role override',
    sourceLegacy: 'Legacy separate model',
    sourceInherited: (role: string) => `Inherited from ${role}`,
    editRole: (role: string) => `Edit ${role}`,
    roleParameters: 'Role Parameters',
    parametersByModelNotice: 'Separate Parameters by Model is enabled; role parameters are not used for this role.',
    parametersDisabledNotice: 'Separate parameters are off globally.',
    fallbackModels: 'Fallback Models',
    noFallbackModels: 'No fallback models configured.',
    fallbackUnsupported: 'Fallback models are not supported for this role.',
    legacySeparateModels: 'Use Legacy Separate Models',
    roles: {
      chatMain: 'Main Chat',
      chatAux: 'Auxiliary',
      translate: 'Translation',
      memory: 'Memory Summary',
      emotion: 'Emotion',
      otherAx: 'Other Auxiliary',
      scriptMain: 'Script Main',
      scriptAux: 'Script Auxiliary',
    },
    descriptions: {
      chatMain: 'Primary chat generation.',
      chatAux: 'Default auxiliary generation.',
      translate: 'Translation requests.',
      memory: 'Memory summary requests.',
      emotion: 'Emotion image analysis.',
      otherAx: 'General auxiliary requests.',
      scriptMain: 'Script requests that use the main role.',
      scriptAux: 'Script requests that use the auxiliary role.',
    },
  },
  scriptModelOverrides: {
    title: 'Script model overrides',
    description:
      'Choose local model profiles for LLM and axLLM calls owned by this item. These selections are not included in individual character or module exports.',
    llm: 'LLM model profile',
    axLlm: 'axLLM model profile',
    inheritRole: 'Use the configured script role',
  },
  modelProfiles: {
    settingsTitle: 'Model',
    defaultChatSettingsTitle: 'Default Chat Configuration',
    defaultChatSettingsDescription:
      'Every imported character and new chat starts from this database-level configuration. Existing chats keep their own snapshot.',
    defaultChatModelPreset: 'Default model preset',
    defaultChatPromptPreset: 'Default prompt preset',
    defaultChatPersona: 'Default persona',
    defaultChatAgentPreset: 'Default agent preset',
    defaultChatJailbreak: 'Enable jailbreak by default',
    rolesTab: 'Roles',
    profilesTab: 'Profiles',
    credentialsTab: 'API Credentials',
    presetsTab: 'Presets',
    rolesTabTitle: 'Role Bindings',
    rolesTabDescription:
      'Bind each role to a durable profile, inherit from a source role, or keep legacy compatibility.',
    profilesTabTitle: 'Profiles',
    profilesTabDescription: 'Reusable provider and model records used by role bindings.',
    credentialsTabTitle: 'API Credentials',
    credentialsTabDescription: 'Store provider secrets once and reference them from model profiles.',
    presetsTabTitle: 'Model Presets',
    presetsTabDescription:
      'Save and apply role binding sets that decide which durable profiles each generation role uses.',
    saveCurrentRolesAsPreset: 'Save Current Roles',
    createEmptyModelPreset: 'Create Empty Preset',
    updateFromCurrentRoles: 'Update Roles',
    defaultPresetName: (index: number) => `Model Preset ${index}`,
    noModelPresets: 'No model presets yet.',
    promptPresetRoleOverrideNotice: (name: string) =>
      `The selected prompt preset "${name}" also stores role bindings and may override the applied model preset during generation.`,
    chatRolesColumn: 'Chat Roles',
    roleBindingsColumn: 'Role Bindings',
    storedDataColumn: 'Stored Data',
    moveUp: 'Up',
    moveDown: 'Down',
    modelPresetLegacySummary: 'Legacy model preset',
    modelPresetNoRoleBindings: 'No durable role bindings',
    modelPresetRoleBindingSummary: (
      profileCount: number,
      inheritCount: number,
      legacyCount: number,
      missingCount: number,
    ) =>
      `${profileCount} profile, ${inheritCount} inherited, ${legacyCount} legacy${
        missingCount ? `, ${missingCount} missing` : ''
      }`,
    modelPresetLegacyFieldSummary: (count: number) => `${count} legacy model field${count === 1 ? '' : 's'}`,
    modelPresetRoleBindingsBadge: 'Role bindings',
    modelPresetEmbeddedProfilesBadge: 'Embedded profiles',
    modelPresetRuntimeBadge: 'Runtime defaults',
    modelPresetLegacyBadge: 'Legacy fields',
    modelPresetEmptyBadge: 'Empty',
    updateModelPresetFromCurrentConfirm: (name: string) => `Update "${name}" with the current role bindings?`,
    deleteModelPresetConfirm: (name: string) => `Delete "${name}"?`,
    runtimeDefaultsTitle: 'Runtime Defaults',
    runtimeDefaultsPlaceholder: 'Runtime defaults are stored separately. The full field editor lands in a later phase.',
    runtimeDefaultsEmpty: 'No runtime defaults are configured.',
    runtimeDefaultsSummary: (count: number) => `${count} runtime default${count === 1 ? '' : 's'} configured.`,
    convertPromptTitle: 'Convert legacy model settings?',
    convertPromptDescription:
      'This database only has legacy flat model settings. Convert them into durable profiles before editing roles and profiles here.',
    convertDeclinedNotice: 'Legacy model settings are still active. You can convert them to profiles at any time.',
    convertToProfiles: 'Convert to Profiles',
    converting: 'Converting...',
    notNow: 'Not Now',
    advancedLegacySettings: 'Advanced Legacy Settings',
    advancedLegacyDescription:
      'Legacy flat model fields remain available as compatibility data and conversion inputs. The profile-first workflow above does not write these fields.',
    legacyMainModel: 'Legacy Main Model',
    legacyAuxModel: 'Legacy Auxiliary Model',
    apply: 'Apply',
    applying: 'Applying...',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    edit: 'Edit',
    reset: 'Reset',
    editProfile: 'Edit Profile',
    duplicate: 'Duplicate',
    delete: 'Delete',
    createProfile: 'Create Profile',
    addDivider: 'Add Divider',
    newProfileDefaultName: 'New Profile',
    modelPlaceholder: 'Model id',
    requestModelPlaceholder: 'Request model override',
    providerConfiguration: 'Provider Configuration',
    knownModel: 'Known Model',
    manualModelOption: 'Manual model id',
    apiKeyLabel: 'API Key',
    apiKeyOptionalLabel: 'API Key (optional)',
    savedSecretPlaceholder: 'Saved secret',
    providerChangeClearedCredential:
      "The previous provider's credential reference was cleared. Select a compatible credential for the new provider if required.",
    credentialLabel: 'Credential',
    noCredential: 'No credential',
    missingCredential: (credentialId: string) => `Missing credential: ${credentialId}`,
    createNewCredential: 'Create new credential',
    createCredential: 'Create Credential',
    editCredential: 'Edit Credential',
    createApiCredential: 'New API Key',
    createVertexCredential: 'New Vertex Service Account',
    newApiCredentialName: 'New API Key',
    newVertexCredentialName: 'New Vertex Service Account',
    credentialName: 'Name',
    credentialType: 'Type',
    apiKeyCredentialType: 'API key',
    vertexCredentialType: 'Vertex service account',
    noCredentials: 'No provider credentials yet.',
    notUsedByProfiles: 'Not used by any profile',
    credentialInUseShort: 'This credential is used by a model profile.',
    credentialInUse: (profiles: string) => `This credential is used by model profiles: ${profiles}`,
    deleteCredentialConfirm: (name: string) => `Delete credential "${name}"?`,
    vertexProjectId: 'Project ID',
    vertexRegion: 'Region',
    vertexClientEmail: 'Client Email',
    vertexPrivateKey: 'Private Key',
    baseUrlLabel: 'Base URL',
    baseUrlPlaceholder: 'https://example.com/v1',
    llmGatewayReasoningEffort: 'Reasoning Effort',
    llmGatewayVerbosity: 'Verbosity',
    llmGatewayServiceTier: 'Service Tier',
    llmGatewayRouting: 'Routing',
    ollamaLocal: 'Ollama Local',
    ollamaCloud: 'Ollama Cloud',
    ollamaModelPlaceholder: 'llama3.1',
    ollamaBaseUrlPlaceholder: 'http://localhost:11434',
    ollamaRequestFormat: 'Request Format',
    ollamaNativeFormat: 'Ollama SDK',
    ollamaOpenAIFormat: 'OpenAI Compatible',
    ollamaResponsesFormat: 'OpenAI Responses',
    ollamaAnthropicFormat: 'Anthropic Messages',
    ollamaThinkingMode: 'Thinking',
    ollamaThinkingOff: 'Off',
    ollamaThinkingOn: 'On',
    ollamaThinkingLow: 'Low',
    ollamaThinkingMedium: 'Medium',
    ollamaThinkingHigh: 'High',
    customApiChatCompletionsWarning:
      'The app appends /chat/completions automatically. Use the API base URL without that suffix.',
    extraHeaders: 'Extra Headers',
    additionalParams: 'Additional Params',
    headerNamePlaceholder: 'Header',
    headerValuePlaceholder: 'Value',
    paramNamePlaceholder: 'Param',
    paramValuePlaceholder: 'Value',
    addHeader: 'Add Header',
    addParam: 'Add Param',
    noExtraHeaders: 'No extra headers.',
    noAdditionalParams: 'No additional params.',
    customTokenizer: 'Tokenizer',
    defaultTokenizer: 'Default tokenizer',
    customApiFlags: 'Capability Flags',
    compatibilityEditNotice:
      'This compatibility profile keeps its existing provider data and only supports rename here.',
    unsupportedProviderEditNotice: (providerId: string) =>
      `Provider "${providerId}" is not editable in this first-class profile editor.`,
    unsupportedProviderLabel: (providerId: string) => `Unsupported: ${providerId}`,
    compatibilityRuntimeNotice: 'Runtime overrides and fallbacks are only editable for first-class provider profiles.',
    runtimeOverridesTitle: 'Runtime Overrides',
    fallbacksTitle: 'Fallbacks',
    discardProfileChangesConfirm: 'Discard unsaved profile changes?',
    fallbackProfileMode: 'Profile',
    fallbackRawModelMode: 'Raw Model',
    fallbackModeLabel: (index: number) => `Fallback ${index} mode`,
    fallbackProfileLabel: (index: number) => `Fallback ${index} profile`,
    fallbackModelLabel: (index: number) => `Fallback ${index} model`,
    removeFallbackLabel: (index: number) => `Remove fallback ${index}`,
    noFallbacks: 'No fallbacks configured.',
    addFallbackProfile: 'Add Fallback Profile',
    addRawModelFallback: 'Add Raw Model',
    runtimeNumberSection: 'Numbers',
    runtimeBooleanSection: 'Toggles',
    runtimeTextSection: 'Text',
    runtimeCustomFlagsSection: 'Custom Flags',
    runtimeUnset: 'Unset',
    runtimeTrue: 'True',
    runtimeFalse: 'False',
    commaSeparatedPlaceholder: 'Comma-separated values',
    runtimeFields: {
      maxContext: 'Max Context',
      maxResponse: 'Max Response',
      temperature: 'Temperature',
      topP: 'Top P',
      topK: 'Top K',
      minP: 'Min P',
      topA: 'Top A',
      repetitionPenalty: 'Repetition Penalty',
      lengthPenalty: 'Length Penalty',
      typicalP: 'Typical P',
      noRepeatNgramSize: 'No Repeat n-gram Size',
      tailFreeSampling: 'Tail-free Sampling',
      repetitionPenaltyRange: 'Repetition Penalty Range',
      repetitionPenaltySlope: 'Repetition Penalty Slope',
      mirostatLearningRate: 'Mirostat Learning Rate',
      mirostatTau: 'Mirostat Tau',
      cfgScale: 'CFG Scale',
      frequencyPenalty: 'Frequency Penalty',
      presencePenalty: 'Presence Penalty',
      reasoningEffort: 'Reasoning Effort',
      thinkingTokens: 'Thinking Tokens',
      verbosity: 'Verbosity',
      genTime: 'Generation Time',
      thinkingType: 'Thinking Type',
      deepseekThinkingType: 'DeepSeek Thinking Type',
      adaptiveThinkingEffort: 'Adaptive Thinking Effort',
      deepseekReasoningEffort: 'DeepSeek Reasoning Effort',
      extractJson: 'Extract JSON',
      jsonSchema: 'JSON Schema',
      customTokenizer: 'Custom Tokenizer',
      halfStreaming: 'Half Streaming',
      useStreaming: 'Streaming',
      jsonSchemaEnabled: 'JSON Schema Enabled',
      strictJsonSchema: 'Strict JSON Schema',
      outputImageModal: 'Output Image Modal',
      enableCustomFlags: 'Enable Custom Flags',
      stripCoT: 'Strip CoT',
      modelTools: 'Model Tools',
    },
    commandConflict: 'The server has newer settings. Refresh and try again.',
    commandUnavailable: 'Server commands are unavailable.',
    commandQueued: 'This change is saved on this device and queued. It will retry automatically.',
    commandReplayDiscarded: 'The queued change could not be applied. The server version was restored.',
    editTargetMissing:
      'The profile you were editing no longer exists. Review your draft before closing or creating a new profile.',
    unsavedRoleChanges: 'Role binding changes are not applied yet.',
    noUnsavedRoleChanges: 'Role bindings are up to date.',
    roleColumn: 'Role',
    bindingModeColumn: 'Binding',
    inheritedSourceColumn: 'Inherited Source',
    effectiveProfileColumn: 'Effective Profile',
    providerModelColumn: 'Provider / Model / Request',
    statusColumn: 'Status',
    fallbackColumn: 'Fallbacks',
    profileNameColumn: 'Name',
    providerColumn: 'Provider',
    modelColumn: 'Model',
    requestModelColumn: 'Request Model',
    usedByColumn: 'Used By',
    actionsColumn: 'Actions',
    noProfiles: 'No profiles yet.',
    notUsedByRoles: 'Not used by any role',
    noInheritedSource: 'None',
    compatibilityProfile: 'Legacy compatibility',
    compatibilityProvider: 'Compatibility / inferred',
    missingProfile: (profileId: string) => `Missing profile: ${profileId}`,
    copyName: (name: string) => `${name} Copy`,
    deleteProfileConfirm: (name: string) => `Delete "${name}"?`,
    deleteDividerConfirm: 'Delete this divider?',
    deleteProfileReassignConfirm: (name: string, roles: string) =>
      `Delete "${name}"? Roles using it will be reassigned: ${roles}.`,
    profileUsedByModelPresets: (name: string, presets: string) =>
      `Cannot delete "${name}" because it is used by Model Presets: ${presets}.`,
    dragProfile: 'Drag to reorder profile',
    dragDivider: 'Drag to reorder divider',
    bindingModes: {
      profile: 'Profile',
      inherit: 'Inherit',
      legacy: 'Legacy',
    },
    providerNames: {
      openai: 'OpenAI',
      llmgateway: 'LLM Gateway',
      neuralwatt: 'Neuralwatt',
      anthropic: 'Anthropic',
      google: 'Google',
      vertex: 'Vertex AI',
      ollama: 'Ollama',
      'custom-api': 'Custom API',
      'debug-echo': 'Debug Echo',
    },
    statusBuckets: {
      ready: 'Ready',
      incomplete: 'Incomplete',
      compatibility: 'Compatibility',
      unsupported: 'Unsupported',
    },
    statusReasons: {
      'legacy-mode': 'legacy mode',
      'static-model': 'static model',
      'missing-provider-id': 'missing provider',
      'inferred-provider-id': 'inferred provider',
      'profile-not-found': 'profile not found',
      'profile-model-missing': 'missing model',
      'credential-missing': 'credential not found',
      'api-key-missing': 'missing API key',
      'base-url-missing': 'missing base URL',
      'request-model-missing': 'missing request model',
      'vertex-project-id-missing': 'missing Vertex project',
      'vertex-region-missing': 'missing Vertex region',
      'vertex-client-email-missing': 'missing Vertex client email',
      'vertex-private-key-missing': 'missing Vertex private key',
      'unsupported-provider-id': 'unsupported provider',
      'unsupported-model': 'unsupported model',
      'provider-capability-incomplete': 'provider configuration incomplete',
      'provider-capability-unsupported': 'provider capability unsupported',
    },
  },
  agentPresets: {
    settingsTitle: 'Agent Presets',
    settingsDescription: 'Compose reusable Agents into auxiliary generation plans.',
    agentsTitle: 'Agents',
    agentsDescription: 'Reusable behavior definitions shared across Agent Presets.',
    emptyAgents: 'No Agents yet.',
    createAgent: 'Create Agent',
    editAgent: 'Edit Agent',
    newAgentName: 'New Agent',
    agentUsageCount: (count: number) => `Used by ${count} preset invocation${count === 1 ? '' : 's'}`,
    deleteAgentConfirm: (name: string) => `Delete Agent “${name}”?`,
    selectAgent: 'Select Agent',
    addAgent: 'Add to Preset',
    noAgentsAvailable: 'Create an Agent before adding one to a preset.',
    invocationsTitle: 'Preset Agents',
    noInvocationsInPhase: 'No Agents in this phase.',
    editInvocation: 'Edit Preset Use',
    saveInvocation: 'Save Preset Use',
    removeInvocation: 'Remove',
    invocationEnabled: 'Enabled in this preset',
    modelOverride: 'Override Agent model',
    runtimeOverride: 'Override Agent runtime',
    structuredOutputStrict: 'Strict structured output',
    agentDefaultsHelp: 'Instructions, prepared inputs, output format, and defaults are edited in the Agent library.',
    chatSelectionLabel: 'Agent Preset',
    noSelected: 'No Agent Preset',
    noDefault: 'No default',
    globalDefault: 'Global default',
    globalDefaultHelp: 'New chats can start with this preset when chat setup applies defaults.',
    presetsSectionTitle: 'Presets',
    presetsSectionDescription: 'Ordered list of Agent Presets available to chats.',
    emptyState: 'No Agent Presets yet.',
    createPreset: 'Create Preset',
    editPreset: 'Edit Preset',
    duplicate: 'Duplicate',
    delete: 'Delete',
    edit: 'Edit',
    moveUp: 'Up',
    moveDown: 'Down',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving...',
    commandQueued: 'Saved locally and queued. You do not need to submit this change again.',
    commandBlocked: 'Wait for the queued Agent Preset change to finish syncing, then try again.',
    newPresetName: 'New Agent Preset',
    nameLabel: 'Name',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Optional author note',
    moduleIntegrationLabel: 'Module Integration',
    moduleIntegrationDescription:
      'Comma-separated module IDs or namespaces activated while this Agent Preset is selected.',
    moduleIntegrationPlaceholder: 'research-tools, module-id',
    enabledLabel: 'Enabled',
    limitConcurrency: 'Limit concurrency',
    maxConcurrency: 'Max Concurrency',
    finalOutputTemplateLabel: 'Final output CBS',
    finalOutputTemplateDescription:
      'Optional CBS evaluated after the main response and all Agents complete. It takes precedence over a direct Final output destination.',
    finalOutputTemplatePlaceholder: '{{slot::mainOutput}}\n\nStatus:\n{{agent::status}}',
    finalOutputVariablesLabel: 'Available values:',
    unlimited: 'Unlimited',
    defaultBadge: 'Default',
    statusReady: 'Ready',
    statusDisabled: 'Disabled',
    statusInvalid: 'Invalid',
    statusIncomplete: 'Incomplete',
    statusModelNotReady: 'Model not ready',
    missingSelectedShort: 'Missing Agent Preset',
    missingSelected: (id: string) => `Missing Agent Preset: ${id}`,
    beforeMain: 'Before Main',
    afterMain: 'After Main',
    stepCount: (count: number) => `${count} enabled Agent use${count === 1 ? '' : 's'}`,
    phaseSummary: (beforeMain: number, afterMain: number) => `${beforeMain} before-main, ${afterMain} after-main`,
    noStepsInPhase: 'No steps in this phase.',
    stepsTitle: 'Steps',
    createStep: 'New Step',
    editStep: 'Edit Step',
    saveStep: 'Save Step',
    cancelStep: 'Cancel Step',
    defaultStepName: (index: number) => `Step ${index}`,
    stepNameLabel: 'Step name',
    stepEnabledLabel: 'Step enabled',
    stepPhaseLabel: 'Phase',
    instructionLabel: 'Instruction',
    useChatMLLabel: 'Use ChatML',
    useChatMLDescription:
      'Parse this instruction as role-tagged ChatML and send those messages without the default Agent system prefill.',
    invalidChatMLInstruction: 'A ChatML instruction must start with <|im_start|>.',
    agentTogglesLabel: 'Configurable toggles',
    agentTogglesDescription:
      'Values are stored per chat in an Agent-specific namespace and are available only to this Agent.',
    addToggle: 'Add toggle',
    removeToggle: 'Remove toggle',
    noAgentToggles: 'This Agent does not define any toggles.',
    localKeyLabel: 'Local key',
    toggleLabelLabel: 'Display label',
    toggleKindLabel: 'Control type',
    toggleKindLabels: {
      boolean: 'Boolean',
      select: 'Select',
      text: 'Text',
      textarea: 'Long text',
    },
    toggleOptionsLabel: 'Options (comma-separated)',
    togglePlaceholder: (key: string) => `Use in the instruction as {{agentToggle::${key}}}`,
    lorebookInputsLabel: 'Required lorebook inputs',
    lorebookInputsDescription:
      'The chat is searched first, then the character. Matching entries must be marked Agent input only.',
    addLorebookInput: 'Add lorebook input',
    removeLorebookInput: 'Remove lorebook input',
    noLorebookInputs: 'This Agent does not require an Agent-only lorebook entry.',
    lorebookDisplayNameLabel: 'Lorebook display name',
    lorebookInputPlaceholder: (key: string) => `Place in the instruction as {{agentInput::${key}}}`,
    modelModeLabel: 'Model',
    inheritMainModel: 'Inherit main model',
    selectedModelProfile: 'Model profile',
    modelProfileLabel: 'Selected model profile',
    noModelProfiles: 'No model profiles',
    dependenciesLabel: 'Dependencies',
    noDependencyOptions: 'No available dependencies.',
    outputFormatLabel: 'Output format',
    outputFormatText: 'Free text',
    outputFormatJsonObject: 'JSON object',
    destinationLabel: 'Destination',
    destinationPromptOutput: 'Prompt output',
    destinationIntermediate: 'Intermediate',
    destinationUserInput: 'User input',
    destinationFinalOutput: 'Final output',
    failurePolicyLabel: 'Failure policy',
    failurePolicyRequired: 'Required',
    failurePolicyOptional: 'Optional',
    failurePolicyFallbackText: 'Fallback text',
    failurePolicyStopGeneration: 'Stop generation',
    fallbackTextLabel: 'Fallback text',
    timeoutMsLabel: 'Timeout (ms)',
    maxInputCharsLabel: 'Max input chars',
    maxOutputCharsLabel: 'Max output chars',
    temperatureLabel: 'Temperature',
    preparedInputScopesLabel: 'Prepared inputs',
    preparedInputScopesDescription:
      'Select the context this Agent can use. A selected input is included only where its instruction placeholder appears.',
    preparedInputCbsNameLabel: 'Available prepared-input CBS variables',
    inputScopeLabels: {
      recentChatTail: 'Recent chat tail',
      chatSearchSnippets: 'Chat search snippets',
      lorebookContext: 'Lorebook context',
      memoryContext: 'Memory context',
      characterSummary: 'Character summary',
      personaSummary: 'Persona summary',
      currentUserMessage: 'Current user message',
      previousAgentOutputs: 'Previous agent outputs',
      mainDraft: 'Main draft',
    },
    inputScopeDescriptions: {
      recentChatTail: 'The 12 most recent messages in the current chat, with each message’s role and text.',
      chatSearchSnippets:
        'Up to 6 matching messages from all character chats, found by searching with the current user message.',
      lorebookContext:
        'Lorebook entries activated for the current character and chat, including applicable global entries.',
      memoryContext:
        'Saved context from the current chat, including its last memory, Hypa or Supa memory messages, and Hypa V3 data.',
      characterSummary: 'The current character’s name, description, personality, scenario, prompts, and creator notes.',
      personaSummary: 'The selected persona’s name and prompt, plus the current username.',
      currentUserMessage: 'The latest user message submitted for this generation.',
      previousAgentOutputs:
        'Outputs completed by eligible earlier Agent uses, labeled with their phase, output key, and Agent name.',
      mainDraft: 'The main model’s draft after output edits. Available only when this Agent is used after main.',
    },
    invalidOutputKey: 'Use letters, numbers, or underscores; start with a letter or underscore.',
    savePresetBeforeSteps: 'Save the preset before adding Agents.',
    outputKey: 'Output key',
    diagnostics: 'Diagnostics',
    diagnosticsPending: 'No Agent Preset diagnostics are available yet.',
    diagnosticsDescription: 'Inspect hidden Agent Preset results from generated messages.',
    diagnosticsAvailable: (count: number) => `${count} diagnostic run${count === 1 ? '' : 's'} available.`,
    openDiagnostics: 'Open Diagnostics',
    closeDiagnostics: 'Close Diagnostics',
    retryDiagnostics: 'Retry',
    diagnosticsLoading: 'Loading chat history...',
    diagnosticsLoadError: 'Could not load all chat diagnostics.',
    diagnosticsHistory: 'Run history',
    diagnosticsRunCount: (count: number) => `${count} run${count === 1 ? '' : 's'}`,
    diagnosticsLimited: (shown: number, total: number) => `Showing the newest ${shown} of ${total} runs.`,
    diagnosticsUnknownCharacter: 'Unknown character',
    diagnosticsUnknownChat: 'Unknown chat',
    diagnosticsUnknownTime: 'Time unavailable',
    diagnosticsCompletedWithSkips: 'Completed with skipped steps',
    diagnosticsOverview: 'Run overview',
    diagnosticsPresetVersion: 'Preset version',
    diagnosticsGenerationId: 'Generation ID',
    diagnosticsModel: 'Main model',
    diagnosticsPlanStatus: 'Plan status',
    diagnosticsPhaseCounts: 'Planned steps',
    diagnosticsFinalTextModified: 'Final text modified',
    diagnosticsFinalOutputComposed: 'Final output CBS applied',
    diagnosticsPromptOutputKeys: 'Prompt output keys',
    diagnosticsNoPromptOutputKeys: 'None',
    diagnosticsRunFailure: 'Run failure',
    diagnosticsMainOutputPreview: 'Main output preview',
    diagnosticsStepResults: 'Step results',
    diagnosticsNoStepResults: 'No step result rows were recorded.',
    diagnosticsStepUnknown: 'Unknown step',
    diagnosticsDuration: 'Duration',
    diagnosticsProvider: 'Provider',
    diagnosticsProfile: 'Profile',
    diagnosticsStepModel: 'Step model',
    diagnosticsInputChars: 'Input chars',
    diagnosticsOutputChars: 'Output chars',
    diagnosticsParseStatus: 'Parse status',
    diagnosticsOutputPreview: 'Hidden output preview',
    diagnosticsPreparedInputs: 'Prepared inputs',
    diagnosticsPreparedInputIssues: 'Prepared input notes',
    diagnosticsTruncated: 'truncated',
    diagnosticsYes: 'Yes',
    diagnosticsNo: 'No',
    diagnosticsUnknown: 'Unknown',
    diagnosticStepStatuses: {
      success: 'Succeeded',
      failed: 'Failed',
      skipped: 'Skipped',
      unknown: 'Unknown',
    },
    progressBeforeMain: 'Before main response',
    progressAfterMain: 'After main response',
    progressLabel: (presetName: string, phase: string, completed: number, total: number) =>
      `Agent Preset: ${presetName} · ${phase} · ${completed}/${total} steps`,
    progressActiveSteps: (stepNames: string) => `Running: ${stepNames}`,
    progressWaiting: 'Preparing the next step',
    editorNotice: 'Metadata and step authoring.',
    nameColumn: 'Name',
    statusColumn: 'Status',
    stepsColumn: 'Steps',
    usageColumn: 'Usage',
    actionsColumn: 'Actions',
    usageCount: (count: number) => `${count} use${count === 1 ? '' : 's'}`,
    copyName: (name: string) => `${name} Copy`,
    deletePresetConfirm: (name: string) => `Delete "${name}"?`,
    deleteStepConfirm: (name: string) => `Remove Agent “${name}” from this preset?`,
    discardChangesConfirm: 'Discard unsaved Agent Preset changes?',
    discardStepChangesConfirm: 'Discard unsaved step changes?',
    editTargetMissing: 'The Agent Preset you were editing no longer exists.',
    editStepTargetMissing: 'The Agent Preset step you were editing no longer exists.',
    commandConflict: 'The server has newer settings. Refresh and try again.',
    commandUnavailable: 'Server commands are unavailable.',
  },
} satisfies I18nTranslation

type I18nTranslationFunction = (...args: any[]) => string

type I18nTranslation = {
  [key: string]: string | I18nTranslation | string[] | I18nTranslationFunction
}
